import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.home');
import { ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import { Service } from "src/libs/portal/season/service";

const FILTER_PLAN_API = Object.freeze({
    action: 'filter_plan',
    endpoint: '/wiz/api/page.home/filter_plan'
});
const FILTER_PLAN_SCHEMA_VERSION = '1.1';

const FACE_LANDMARK_SPIKE = Object.freeze({
    queryParameter: 'face-spike',
    packageVersion: '0.10.35',
    assetRoot: '/assets/mediapipe/0.10.35',
    loaderScriptPath: '/assets/mediapipe/0.10.35/vision_bundle.mjs',
    wasmRoot: '/assets/mediapipe/0.10.35/wasm',
    modelAssetPath: '/assets/mediapipe/face-landmarker/float16/1/face_landmarker.task',
    workerScriptPath: '/assets/workers/face-landmarker.worker.js',
    assetLoadTimeoutMs: 30000,
    workerInitializationTimeoutMs: 45000,
    desktopInferenceLongEdge: 320,
    mobileInferenceLongEdge: 256,
    desktopTargetFps: 15,
    mobileTargetFps: 12,
    minimumTargetFps: 3,
    desktopInferenceDutyCycle: 0.45,
    mobileInferenceDutyCycle: 0.35,
    poseCheckIntervalMs: 200,
    overlaySmoothingMs: 72,
    overlayFadeMs: 180,
    metricWindowMs: 2000,
    uiUpdateIntervalMs: 500
});

type CameraState = 'idle' | 'requesting' | 'active' | 'off' | 'denied' | 'unavailable' | 'unsupported' | 'error';
type FaceTrackerState = 'disabled' | 'camera_off' | 'loading' | 'ready' | 'tracking' | 'no_face' | 'multiple_faces' | 'rotated' | 'degraded' | 'error';
type FaceInitializationState = 'idle' | 'asset_loading' | 'worker_initializing' | 'model_ready' | 'fallback' | 'failed' | 'cancelled';
type FaceDelegate = 'GPU' | 'CPU' | 'unknown';
type FaceInferenceBackend = 'worker' | 'main-thread';
type FaceMapRegion = 'eyes' | 'eyebrows' | 'nose' | 'lips' | 'jaw';

interface CameraDeviceOption {
    deviceId: string;
    label: string;
}

interface FaceMapPointDefinition {
    id: string;
    index: number;
    region: FaceMapRegion;
    label: string;
}

interface FaceMapPoint extends FaceMapPointDefinition {
    x: number;
    y: number;
    z: number;
    offsetX: number;
    offsetY: number;
}

interface FaceMapState {
    version: '1.0';
    createdAt: string;
    source: 'mediapipe-local';
    frameWidth: number;
    frameHeight: number;
    points: Record<string, FaceMapPoint>;
    regions: Record<FaceMapRegion, string[]>;
}

interface FaceMapFilterCommand {
    region: FaceMapRegion;
    filterId: string;
    label: string;
    path: string;
    max: number;
    value: number;
    tone: string;
    direction?: number;
}

interface FaceMapCommand extends FaceMapFilterCommand {
    response: string;
    companions?: FaceMapFilterCommand[];
}

interface FacePatchOptions {
    center: { x: number; y: number };
    radiusX: number;
    radiusY: number;
    scaleX?: number;
    scaleY?: number;
    translateX?: number;
    translateY?: number;
    rotation?: number;
    feather?: number;
    filter?: string;
    opacity?: number;
}

const FACE_MAP_STORAGE_KEY = 'meeo.faceMap.v1';
const FACE_BEAUTY_RENDERER = Object.freeze({
    strengthExponent: 0.62,
    patchFeather: 0.38,
    eyeMaxScaleX: 0.085,
    eyeMaxScaleY: 0.11,
    lipMaxScaleX: 0.035,
    lipMaxScaleY: 0.13,
    noseMaxScaleY: 0.055,
    jawMaxLocalScale: 0.055,
    jawMaxInwardShift: 0.036,
    jawVlineMaxInwardShift: 0.032,
    chinMaxTaperScale: 0.065
});

const FACE_MAP_POINT_DEFINITIONS: FaceMapPointDefinition[] = [
    { id: 'leftEyeOuter', index: 33, region: 'eyes', label: '왼쪽 눈 바깥' },
    { id: 'leftEyeInner', index: 133, region: 'eyes', label: '왼쪽 눈 안쪽' },
    { id: 'rightEyeInner', index: 362, region: 'eyes', label: '오른쪽 눈 안쪽' },
    { id: 'rightEyeOuter', index: 263, region: 'eyes', label: '오른쪽 눈 바깥' },
    { id: 'leftEyebrow', index: 105, region: 'eyebrows', label: '왼쪽 눈썹' },
    { id: 'rightEyebrow', index: 334, region: 'eyebrows', label: '오른쪽 눈썹' },
    { id: 'noseBridge', index: 168, region: 'nose', label: '콧대' },
    { id: 'noseTip', index: 1, region: 'nose', label: '코끝' },
    { id: 'noseLeft', index: 98, region: 'nose', label: '왼쪽 콧방울' },
    { id: 'noseRight', index: 327, region: 'nose', label: '오른쪽 콧방울' },
    { id: 'mouthLeft', index: 61, region: 'lips', label: '입술 왼쪽' },
    { id: 'upperLip', index: 13, region: 'lips', label: '윗입술' },
    { id: 'mouthRight', index: 291, region: 'lips', label: '입술 오른쪽' },
    { id: 'lowerLip', index: 14, region: 'lips', label: '아랫입술' },
    { id: 'jawLeft', index: 234, region: 'jaw', label: '왼쪽 턱선' },
    { id: 'chin', index: 152, region: 'jaw', label: '턱끝' },
    { id: 'jawRight', index: 454, region: 'jaw', label: '오른쪽 턱선' }
];

const FACE_MAP_REGIONS: Record<FaceMapRegion, string[]> = {
    eyes: ['leftEyeOuter', 'leftEyeInner', 'rightEyeInner', 'rightEyeOuter'],
    eyebrows: ['leftEyebrow', 'rightEyebrow'],
    nose: ['noseBridge', 'noseTip', 'noseLeft', 'noseRight'],
    lips: ['mouthLeft', 'upperLip', 'mouthRight', 'lowerLip'],
    jaw: ['jawLeft', 'chin', 'jawRight']
};

const FACE_MAP_GUIDES = [
    { id: 'left-eye', pointIds: ['leftEyeOuter', 'leftEyeInner'] },
    { id: 'right-eye', pointIds: ['rightEyeInner', 'rightEyeOuter'] },
    { id: 'eyebrows', pointIds: FACE_MAP_REGIONS.eyebrows },
    { id: 'nose', pointIds: ['noseBridge', 'noseTip', 'noseLeft', 'noseRight'] },
    { id: 'lips', pointIds: FACE_MAP_REGIONS.lips },
    { id: 'jaw', pointIds: FACE_MAP_REGIONS.jaw }
];

const FACE_MAP_COMMANDS: Array<FaceMapCommand & { pattern: RegExp }> = [
    {
        region: 'eyes',
        pattern: /(눈.{0,8}(키워|크게|확대)|큰\s*눈)/i,
        filterId: 'face-eyes-scale',
        label: '눈 영역 확대',
        path: 'face.eyes.size',
        max: 0.12,
        value: 56,
        tone: 'lilac',
        response: '보정한 FaceMap의 눈 영역을 기준으로 눈 표현을 키웠어요.'
    },
    {
        region: 'lips',
        pattern: /(입술.{0,8}(도톰|볼륨|두껍)|도톰.{0,4}입술)/i,
        filterId: 'face-lips-volume',
        label: '입술 볼륨',
        path: 'face.lips.volume',
        max: 0.18,
        value: 52,
        tone: 'apricot',
        response: '보정한 FaceMap의 입술 영역에 자연스러운 볼륨 표현을 적용했어요.'
    },
    {
        region: 'nose',
        pattern: /(코.{0,8}(높여|높게|오똑)|콧대.{0,8}(높여|살려))/i,
        filterId: 'face-nose-highlight',
        label: '콧대 하이라이트',
        path: 'face.nose.highlight',
        max: 0.30,
        value: 48,
        tone: 'sand',
        response: '보정한 FaceMap의 코 기준점을 따라 콧대 표현을 높였어요.'
    },
    {
        region: 'jaw',
        pattern: /(V\s*라인|VLINE|브이라인)/i,
        filterId: 'face-jaw-vline',
        label: 'V라인',
        path: 'face.jaw.vline',
        max: 0.12,
        value: 62,
        tone: 'sage',
        response: '보정한 FaceMap의 하관과 턱끝 기준점을 따라 자연스러운 V라인을 적용했어요.',
        companions: [
            {
                region: 'jaw',
                filterId: 'face-jaw-width',
                label: '턱선 너비',
                path: 'face.jaw.width',
                max: 0.14,
                value: 42,
                tone: 'sage',
                direction: -1
            },
            {
                region: 'jaw',
                filterId: 'face-chin-taper',
                label: '턱끝 테이퍼',
                path: 'face.chin.taper',
                max: 0.10,
                value: 56,
                tone: 'sage'
            }
        ]
    },
    {
        region: 'jaw',
        pattern: /(하관.{0,8}(줄여|축소|좁혀|갸름)|(줄여|축소|좁혀).{0,5}하관)/i,
        filterId: 'face-jaw-width',
        label: '턱선 너비',
        path: 'face.jaw.width',
        max: 0.14,
        value: 60,
        tone: 'sage',
        direction: -1,
        response: '보정한 FaceMap의 하관 외곽만 안쪽으로 이동해 하관을 줄였어요.',
        companions: [
            {
                region: 'jaw',
                filterId: 'face-jaw-vline',
                label: 'V라인',
                path: 'face.jaw.vline',
                max: 0.12,
                value: 45,
                tone: 'sage'
            },
            {
                region: 'jaw',
                filterId: 'face-chin-taper',
                label: '턱끝 테이퍼',
                path: 'face.chin.taper',
                max: 0.10,
                value: 40,
                tone: 'sage'
            }
        ]
    },
    {
        region: 'jaw',
        pattern: /(턱선?.{0,8}(갸름|슬림|좁게|좁혀)|(갸름|슬림).{0,5}턱)/i,
        filterId: 'face-jaw-width',
        label: '턱선 너비',
        path: 'face.jaw.width',
        max: 0.14,
        value: 54,
        tone: 'sage',
        direction: -1,
        response: '보정한 FaceMap의 턱선을 기준으로 갸름한 윤곽 표현을 적용했어요.'
    },
    {
        region: 'eyebrows',
        pattern: /(눈썹.{0,8}(올려|높여|리프트)|브로우.{0,8}(리프트|올려))/i,
        filterId: 'face-brows-lift',
        label: '눈썹 리프트',
        path: 'face.brows.lift',
        max: 0.10,
        value: 52,
        tone: 'lilac',
        response: '보정한 FaceMap의 눈썹 영역에 낮은 강도의 리프트 표현을 적용했어요.'
    }
];

const FACE_OVERLAY_LANDMARK_INDICES = Array.from(new Set([
    1, 33, 61, 63, 66, 70, 93, 107, 132, 133, 136, 144, 148, 149, 150, 152, 153, 158, 160, 172, 176,
    234, 263, 288, 291, 323, 361, 362, 365, 373, 377, 378, 379, 380, 385, 387, 397,
    293, 296, 300, 336, 400, 454,
    ...FACE_MAP_POINT_DEFINITIONS.map((point) => point.index)
]));

@Component({
    selector: 'wiz-page-home',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.home/view.scss */
:host {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 100vh;
}

* {
  box-sizing: border-box;
}

button,
input {
  font: inherit;
}

button {
  -webkit-tap-highlight-color: transparent;
}

.meeo-app {
  --ink: #17211c;
  --muted: #67716b;
  --line: #dfe3dd;
  --paper: #f5f4ef;
  --panel: #fbfbf8;
  --white: #ffffff;
  --sage: #a9c5b5;
  --sage-deep: #416b5a;
  --apricot: #efb48e;
  --lilac: #b9aecb;
  --sand: #d2b889;
  position: relative;
  height: 100vh;
  min-height: 720px;
  overflow: hidden;
  color: var(--ink);
  background: var(--paper);
  font-family: "Pretendard Variable", "Pretendard", "SUIT Variable", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  letter-spacing: -0.02em;
}

.topbar {
  height: 68px;
  padding: 0 28px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  border-bottom: 1px solid rgba(23, 33, 28, 0.1);
  background: rgba(245, 244, 239, 0.96);
}

.brand {
  justify-self: start;
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  color: var(--ink);
  text-decoration: none;
}

.brand-word {
  font-size: 21px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: -0.055em;
}

.brand-dot {
  width: 6px;
  height: 6px;
  margin-top: -1px;
  border-radius: 50%;
  background: var(--apricot);
}

.topbar-center {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #727a75;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
}

.prototype-label {
  color: var(--sage-deep);
}

.status-divider {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #a4aaa6;
}

.status-copy {
  letter-spacing: 0;
}

.privacy-chip {
  justify-self: end;
  min-height: 40px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #d8ded9;
  border-radius: 999px;
  color: #4e5e55;
  background: #fafaf7;
  font-size: 12px;
  font-weight: 650;
  cursor: help;
}

.privacy-chip svg {
  width: 16px;
  height: 16px;
}

.workspace {
  height: calc(100vh - 68px);
  min-height: 652px;
  padding: 18px 24px 24px;
  display: grid;
  grid-template-columns: minmax(0, 1.62fr) minmax(400px, 0.78fr);
  gap: 18px;
}

.mirror-stage {
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-radius: 26px;
  background: #25322b;
  box-shadow: 0 18px 50px rgba(38, 49, 42, 0.12);
  isolation: isolate;
}

.mirror-media,
.mirror-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.mirror-media {
  overflow: hidden;
  background: radial-gradient(circle at 50% 42%, rgba(149, 180, 163, 0.18) 0 13%, transparent 42%), linear-gradient(145deg, #27362f 0%, #17221d 100%);
}

.mirror-video {
  object-fit: cover;
  object-position: center;
  opacity: 0;
  transform: scaleX(-1) scale(1.015);
  transition: filter 260ms ease, opacity 220ms ease, transform 350ms ease;
}

.mirror-media.camera-active .mirror-video {
  opacity: 1;
}

.mirror-media.is-original .mirror-video {
  transform: scaleX(-1) scale(1);
}

.landmark-canvas {
  position: absolute;
  z-index: 1;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
  transform: scale(1.015);
  transform-origin: center;
  transition: opacity 180ms ease, transform 350ms ease;
}

.landmark-canvas.is-visible {
  opacity: 1;
}

.mirror-media.is-original .landmark-canvas {
  transform: scale(1);
}

.capture-canvas {
  display: none;
}

.camera-state {
  position: absolute;
  z-index: 3;
  top: 50%;
  left: 50%;
  width: min(440px, 100% - 48px);
  padding: 34px 30px 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: white;
  text-align: center;
  transform: translate(-50%, -52%);
}

.camera-state-icon {
  width: 76px;
  height: 76px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(224, 239, 230, 0.2);
  border-radius: 26px;
  color: #d6e8dd;
  background: rgba(224, 239, 230, 0.08);
  box-shadow: 0 18px 46px rgba(4, 10, 7, 0.18);
}

.camera-state-icon svg {
  width: 42px;
  height: 42px;
}

.camera-state-icon.is-loading {
  animation: cameraPulse 1.25s ease-in-out infinite;
}

@keyframes cameraPulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.72;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
}
.camera-state-kicker {
  margin-top: 22px;
  color: #b9d3c5;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.16em;
}

.camera-state h2 {
  margin: 9px 0 8px;
  font-size: clamp(22px, 2.2vw, 32px);
  line-height: 1.2;
  font-weight: 680;
  letter-spacing: -0.045em;
}

.camera-state p {
  max-width: 360px;
  margin: 0;
  color: rgba(238, 244, 240, 0.72);
  font-size: 12px;
  line-height: 1.6;
}

.camera-state > button {
  min-width: 154px;
  min-height: 48px;
  margin-top: 20px;
  padding: 0 16px 0 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px solid rgba(255, 255, 255, 0.94);
  border-radius: 999px;
  color: #20342a;
  background: rgba(255, 255, 255, 0.94);
  font-size: 11px;
  font-weight: 720;
  cursor: pointer;
  transition: background 160ms ease, transform 160ms ease;
}

.camera-state > button:hover:not(:disabled) {
  background: white;
  transform: translateY(-1px);
}

.camera-state > button:disabled {
  color: #8e9993;
  border-color: rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.12);
  cursor: wait;
}

.camera-state > button svg {
  width: 17px;
  height: 17px;
}

.camera-state small {
  margin-top: 12px;
  color: rgba(228, 237, 232, 0.48);
  font-size: 9px;
}

.mood-tint,
.film-grain {
  position: absolute;
  inset: 0;
  pointer-events: none;
  transition: opacity 240ms ease;
}

.mood-tint {
  background: #e9a977;
  mix-blend-mode: soft-light;
}

.film-grain {
  background-image: repeating-radial-gradient(circle at 23% 17%, rgba(255, 255, 255, 0.9) 0 0.7px, rgba(21, 26, 23, 0.4) 0.7px 1.4px, transparent 1.4px 3px);
  background-size: 5px 5px;
  mix-blend-mode: overlay;
}

.mirror-shade {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(12, 18, 15, 0.34) 0%, rgba(12, 18, 15, 0.01) 27%, rgba(12, 18, 15, 0.03) 54%, rgba(12, 18, 15, 0.62) 100%);
  z-index: 2;
}

.capture-flash {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: rgba(255, 255, 255, 0.86);
  animation: shutterFlash 180ms ease-out forwards;
}

@keyframes shutterFlash {
  from {
    opacity: 0.95;
  }
  to {
    opacity: 0;
  }
}
.mirror-topbar {
  position: absolute;
  z-index: 4;
  top: 0;
  left: 0;
  right: 0;
  padding: 22px 22px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.live-state,
.local-note {
  min-height: 34px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.94);
  text-shadow: 0 1px 14px rgba(0, 0, 0, 0.2);
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.1em;
}

.live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #a6afa9;
  box-shadow: 0 0 0 5px rgba(217, 231, 222, 0.1);
}

.live-dot.active {
  background: #bceacb;
  box-shadow: 0 0 0 5px rgba(188, 234, 203, 0.16);
}

.mirror-top-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.camera-picker {
  position: relative;
  min-width: 0;
  max-width: 230px;
  min-height: 38px;
  padding: 0 30px 0 34px;
  display: flex;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 999px;
  color: white;
  background: rgba(28, 36, 31, 0.34);
  backdrop-filter: blur(12px);
}

.camera-picker select {
  width: 100%;
  min-width: 88px;
  border: 0;
  outline: 0;
  color: inherit;
  background: transparent;
  appearance: none;
  font: inherit;
  font-size: 10px;
  font-weight: 650;
  line-height: 1;
  text-overflow: ellipsis;
  cursor: pointer;
}

.camera-picker select:disabled {
  opacity: 0.56;
  cursor: wait;
}

.camera-picker option {
  color: #20342a;
  background: white;
}

.camera-picker-icon,
.camera-picker-chevron {
  position: absolute;
  pointer-events: none;
}

.camera-picker-icon {
  left: 12px;
  width: 15px;
  height: 15px;
}

.camera-picker-chevron {
  right: 11px;
  width: 13px;
  height: 13px;
}

.local-note {
  letter-spacing: -0.01em;
  font-size: 11px;
  font-weight: 550;
}

.local-note svg {
  width: 15px;
  height: 15px;
}

.camera-toggle {
  min-height: 42px;
  padding: 0 14px 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 0;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 720;
  white-space: nowrap;
  cursor: pointer;
  transition: background 160ms ease, border-color 160ms ease, transform 160ms ease;
}

.camera-toggle svg {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}

.camera-toggle.glass {
  color: white;
  background: rgba(28, 36, 31, 0.42);
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(12px);
}

.camera-toggle.glass:hover:not(:disabled) {
  background: rgba(20, 30, 24, 0.62);
  transform: translateY(-1px);
}

.camera-toggle.glass.is-off {
  color: #20342a;
  background: rgba(255, 255, 255, 0.94);
  border-color: rgba(255, 255, 255, 0.94);
}

.camera-toggle.glass:disabled {
  opacity: 0.48;
  cursor: wait;
}

.preview-label {
  position: absolute;
  z-index: 4;
  top: 76px;
  left: 22px;
  padding: 8px 11px;
  display: flex;
  align-items: baseline;
  gap: 8px;
  border-left: 2px solid #d6eadc;
  color: white;
  background: rgba(18, 25, 21, 0.2);
  backdrop-filter: blur(8px);
}

.preview-label.original {
  border-left-color: white;
}

.preview-label span {
  font-size: 12px;
  font-weight: 700;
}

.preview-label small {
  color: rgba(255, 255, 255, 0.7);
  font-size: 10px;
}

.face-spike-panel {
  position: absolute;
  z-index: 5;
  top: 76px;
  right: 22px;
  width: min(320px, 100% - 44px);
  padding: 13px 14px 12px;
  border: 1px solid rgba(214, 234, 220, 0.28);
  border-radius: 15px;
  color: white;
  background: rgba(15, 24, 19, 0.54);
  box-shadow: 0 14px 32px rgba(7, 13, 10, 0.16);
  backdrop-filter: blur(14px);
}

.face-spike-heading {
  display: flex;
  align-items: center;
  gap: 7px;
  color: rgba(229, 240, 233, 0.84);
  font-size: 8px;
  font-weight: 780;
  letter-spacing: 0.11em;
}

.face-spike-heading small {
  margin-left: auto;
  padding: 2px 5px;
  border: 1px solid rgba(191, 226, 205, 0.28);
  border-radius: 999px;
  color: #c8e7d4;
  font-size: 6px;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.face-spike-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #bde6ca;
  box-shadow: 0 0 0 4px rgba(189, 230, 202, 0.12);
}

.face-spike-panel > strong {
  display: block;
  margin-top: 9px;
  font-size: 13px;
  font-weight: 690;
}

.face-spike-panel > p {
  margin: 4px 0 0;
  color: rgba(235, 242, 238, 0.68);
  font-size: 10px;
  line-height: 1.45;
}

.face-spike-actions {
  margin-top: 9px;
  display: flex;
  gap: 6px;
}

.face-spike-actions button {
  min-height: 28px;
  padding: 5px 9px;
  border: 1px solid rgba(214, 234, 220, 0.2);
  border-radius: 9px;
  color: rgba(244, 248, 245, 0.88);
  background: rgba(255, 255, 255, 0.08);
  font-size: 9px;
  font-weight: 650;
  cursor: pointer;
}

.face-spike-actions button:hover {
  background: rgba(255, 255, 255, 0.14);
}

.face-spike-actions button:focus-visible {
  outline: 2px solid rgba(200, 231, 212, 0.78);
  outline-offset: 2px;
}

.face-spike-metrics {
  margin-top: 9px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.face-spike-metrics span {
  padding: 3px 6px;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 999px;
  color: rgba(244, 248, 245, 0.78);
  background: rgba(255, 255, 255, 0.06);
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  font-size: 8px;
  letter-spacing: 0;
}

.face-spike-panel.state-no-face .face-spike-dot,
.face-spike-panel.state-rotated .face-spike-dot {
  background: #efc49f;
  box-shadow: 0 0 0 4px rgba(239, 196, 159, 0.13);
}

.face-spike-panel.state-multiple-faces .face-spike-dot,
.face-spike-panel.state-degraded .face-spike-dot,
.face-spike-panel.state-error .face-spike-dot,
.face-spike-panel.state-failed .face-spike-dot {
  background: #efaaa2;
  box-shadow: 0 0 0 4px rgba(239, 170, 162, 0.14);
}

.face-spike-panel.state-cancelled .face-spike-dot {
  background: #aab4ae;
  box-shadow: 0 0 0 4px rgba(170, 180, 174, 0.13);
}

.face-spike-panel.state-loading .face-spike-dot,
.face-spike-panel.state-fallback .face-spike-dot {
  animation: cameraPulse 1.25s ease-in-out infinite;
}

.mirror-message {
  position: absolute;
  z-index: 4;
  left: 22px;
  bottom: 106px;
  max-width: min(480px, 66%);
  padding: 12px 15px 12px 11px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 15px;
  color: white;
  background: rgba(17, 25, 21, 0.36);
  backdrop-filter: blur(14px);
}

.meeo-avatar {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border-radius: 9px;
  color: #24382e;
  background: #d9eadf;
  font-size: 10px;
  font-weight: 800;
}

.mirror-message p {
  margin: 1px 0 0;
  font-size: 12px;
  line-height: 1.5;
  letter-spacing: -0.01em;
}

.mirror-controls {
  position: absolute;
  z-index: 5;
  left: 22px;
  right: 22px;
  bottom: 22px;
  height: 68px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
}

.comparison-control {
  justify-self: start;
  padding: 5px 6px 5px 10px;
  display: inline-grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 8px;
  border-radius: 16px;
  background: rgba(18, 27, 22, 0.58);
  border: 1px solid rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(14px);
}

.comparison-label {
  width: 42px;
  color: rgba(255, 255, 255, 0.58);
  font-size: 7px;
  line-height: 1.35;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.comparison-control > div {
  padding: 2px;
  display: flex;
  gap: 2px;
  border-radius: 11px;
  background: rgba(3, 9, 6, 0.18);
}

.comparison-control button {
  min-width: 66px;
  min-height: 42px;
  padding: 5px 10px;
  border: 0;
  border-radius: 9px;
  color: rgba(255, 255, 255, 0.68);
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  cursor: pointer;
  transition: color 160ms ease, background 160ms ease, box-shadow 160ms ease;
}

.comparison-control button small {
  font-size: 7px;
  font-weight: 800;
  letter-spacing: 0.09em;
  opacity: 0.65;
}

.comparison-control button strong {
  font-size: 11px;
  font-weight: 720;
}

.comparison-control button.active {
  color: #1e2d25;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 4px 14px rgba(6, 13, 9, 0.18);
}

.shutter {
  position: relative;
  width: 68px;
  height: 68px;
  display: grid;
  place-items: center;
  border: 2px solid rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  cursor: pointer;
  transition: transform 150ms ease;
}

.shutter:hover {
  transform: scale(1.04);
}

.shutter:active {
  transform: scale(0.96);
}

.shutter:disabled {
  opacity: 0.42;
  cursor: not-allowed;
  transform: none;
}

.shutter-inner {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 3px 18px rgba(0, 0, 0, 0.16);
}

.intensity-readout {
  justify-self: end;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: white;
  text-shadow: 0 1px 12px rgba(0, 0, 0, 0.28);
}

.intensity-readout span {
  color: rgba(255, 255, 255, 0.68);
  font-size: 10px;
}

.intensity-readout strong {
  margin-top: 2px;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.04em;
}

.mood-panel {
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border: 1px solid rgba(23, 33, 28, 0.09);
  border-radius: 22px;
  background: var(--panel);
  box-shadow: 0 12px 34px rgba(38, 49, 42, 0.06);
}

.panel-scroll {
  height: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: #c8cec9 transparent;
}

.panel-scroll::-webkit-scrollbar {
  width: 7px;
}

.panel-scroll::-webkit-scrollbar-thumb {
  border-radius: 10px;
  background: #c8cec9;
}

.intro-block,
.face-map-card,
.starter-block,
.conversation-block,
.prompt-form,
.controls-block,
.plan-block,
.panel-footer {
  margin-left: 24px;
  margin-right: 24px;
}

.intro-block {
  padding-top: 28px;
}

.eyebrow {
  display: inline-block;
  color: var(--sage-deep);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.13em;
}

.intro-block h1 {
  margin: 9px 0 8px;
  color: #17211c;
  font-size: clamp(25px, 2.1vw, 34px);
  line-height: 1.16;
  font-weight: 710;
  letter-spacing: -0.055em;
}

.intro-block p {
  max-width: 410px;
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.58;
}

.face-map-card {
  margin-top: 18px;
  padding: 15px;
  border: 1px solid #dce2dd;
  border-radius: 16px;
  background: radial-gradient(circle at 92% 10%, rgba(185, 174, 203, 0.14), transparent 36%), #f6f7f3;
  transition: border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
}

.face-map-card.is-ready {
  border-color: #abc8b7;
  background: radial-gradient(circle at 92% 10%, rgba(169, 197, 181, 0.22), transparent 38%), #f4f8f5;
  box-shadow: inset 0 0 0 1px rgba(169, 197, 181, 0.12);
}

.face-map-card.needs-attention {
  border-color: #e2c9a7;
}

.face-map-card.is-stale {
  border-color: #d8b98e;
  background: radial-gradient(circle at 92% 10%, rgba(218, 183, 137, 0.2), transparent 38%), #faf7f1;
}

.face-map-card.is-stale .face-map-card-icon {
  color: #825f34;
  background: #f1e5d3;
}

.face-map-card.is-stale .face-map-card-heading small {
  color: #744f25;
  background: #f0dfc7;
}

.face-map-card-heading {
  display: grid;
  grid-template-columns: 38px 1fr auto;
  align-items: center;
  gap: 10px;
}

.face-map-card-icon {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  color: #6d5b86;
  background: #ece7f3;
}

.face-map-card.is-ready .face-map-card-icon {
  color: #35614d;
  background: #deece3;
}

.face-map-card-icon svg {
  width: 24px;
  height: 24px;
}

.face-map-card-heading > div:nth-child(2) {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.face-map-card-heading span {
  color: #818a84;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.face-map-card-heading strong {
  color: #29362f;
  font-size: 12px;
  font-weight: 740;
}

.face-map-card-heading small {
  padding: 5px 7px;
  border-radius: 999px;
  color: #6c617d;
  background: #ebe7ef;
  font-size: 8px;
  font-weight: 750;
}

.face-map-card.is-ready .face-map-card-heading small {
  color: #315943;
  background: #dcebe1;
}

.face-map-card > p {
  margin: 12px 0;
  color: #657069;
  font-size: 10px;
  line-height: 1.55;
}

.face-map-primary {
  width: 100%;
  min-height: 42px;
  padding: 0 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid #345c49;
  border-radius: 11px;
  color: white;
  background: #345c49;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: background 150ms ease, transform 150ms ease, opacity 150ms ease;
}

.face-map-primary:hover:not(:disabled) {
  background: #294d3c;
  transform: translateY(-1px);
}

.face-map-primary:disabled {
  cursor: wait;
  opacity: 0.58;
}

.face-map-primary svg {
  width: 16px;
  height: 16px;
}

.face-point-tools {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
}

.face-point-tools button {
  min-height: 42px;
  border: 1px solid #b9c9bf;
  border-radius: 11px;
  color: #365846;
  background: rgba(255, 255, 255, 0.72);
  font-size: 10px;
  font-weight: 720;
  cursor: pointer;
  transition: border-color 150ms ease, background 150ms ease, color 150ms ease;
}

.face-point-toggle {
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
}

.face-point-toggle svg {
  width: 17px;
  height: 17px;
}

.face-point-toggle.active {
  border-color: #345c49;
  color: white;
  background: #345c49;
}

.face-point-edit {
  min-width: 58px;
  padding: 0 12px;
}

.face-point-tools button:hover {
  border-color: #345c49;
}

.face-map-privacy {
  margin-top: 9px;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #858d88;
  font-size: 8px;
}

.face-map-privacy svg {
  width: 14px;
  height: 14px;
  color: #668475;
}

.starter-block {
  margin-top: 24px;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.section-heading h2 {
  margin: 0;
  color: var(--ink);
  font-size: 13px;
  font-weight: 720;
}

.section-heading > span,
.filters-heading > div > span {
  color: #8a928d;
  font-size: 10px;
}

.prompt-grid {
  margin-top: 10px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 7px;
}

.prompt-grid button {
  min-height: 46px;
  padding: 0 11px 0 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid #e0e3de;
  border-radius: 12px;
  color: #39443e;
  background: #ffffff;
  font-size: 11px;
  font-weight: 620;
  text-align: left;
  cursor: pointer;
  transition: border-color 150ms ease, background 150ms ease, transform 150ms ease;
}

.prompt-grid button svg {
  flex: 0 0 15px;
  width: 15px;
  height: 15px;
  color: #89918c;
}

.prompt-grid button:hover:not(:disabled) {
  border-color: #a7c3b2;
  background: #f5faf7;
  transform: translateY(-1px);
}

.prompt-grid button:disabled {
  cursor: wait;
  opacity: 0.5;
}

.conversation-block {
  margin-top: 20px;
  padding: 14px;
  border: 1px solid #e1e4df;
  border-radius: 14px;
  background: #f3f3ef;
}

.message + .message {
  margin-top: 12px;
}

.message span {
  display: block;
  margin-bottom: 4px;
  color: #8b928e;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.message p {
  margin: 0;
  color: #4c5751;
  font-size: 11px;
  line-height: 1.52;
}

.message.user {
  padding-left: 22px;
  border-left: 2px solid #ced4cf;
}

.message.assistant {
  padding-left: 22px;
  position: relative;
  border-left: 2px solid var(--sage);
}

.message.assistant span {
  color: var(--sage-deep);
}

.cancel-link {
  min-height: 32px;
  margin-top: 4px;
  padding: 0;
  border: 0;
  color: #65716a;
  background: transparent;
  font-size: 10px;
  font-weight: 650;
  text-decoration: underline;
  cursor: pointer;
}

.safe-options {
  margin-top: 13px;
  padding-top: 12px;
  border-top: 1px solid #dce0db;
}

.safe-options p {
  margin: 0 0 8px;
  color: #6d756f;
  font-size: 10px;
}

.safe-options > div {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.safe-options button {
  min-height: 36px;
  padding: 0 11px;
  border: 1px solid #b9c9bf;
  border-radius: 999px;
  color: #355746;
  background: #f8fcf9;
  font-size: 10px;
  font-weight: 650;
  cursor: pointer;
}

.prompt-form {
  margin-top: 12px;
}

.prompt-form > label {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.prompt-input-wrap {
  min-height: 52px;
  padding: 5px 5px 5px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #ccd3cd;
  border-radius: 15px;
  background: white;
  box-shadow: 0 4px 16px rgba(45, 56, 49, 0.05);
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.prompt-input-wrap:focus-within {
  border-color: #769c89;
  box-shadow: 0 0 0 3px rgba(118, 156, 137, 0.13);
}

.prompt-input-wrap input {
  min-width: 0;
  flex: 1;
  border: 0;
  outline: 0;
  color: var(--ink);
  background: transparent;
  font-size: 12px;
}

.prompt-input-wrap input::placeholder {
  color: #9aa09c;
}

.prompt-input-wrap button {
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 12px;
  color: white;
  background: #244b3a;
  cursor: pointer;
  transition: background 150ms ease, transform 150ms ease;
}

.prompt-input-wrap button:hover:not(:disabled) {
  background: #173d2d;
  transform: translateX(1px);
}

.prompt-input-wrap button:disabled {
  color: #a9b0ac;
  background: #e6e9e6;
  cursor: not-allowed;
}

.prompt-input-wrap button svg {
  width: 19px;
  height: 19px;
}

.prompt-form > p {
  margin: 7px 2px 0;
  color: #939a96;
  font-size: 9px;
  line-height: 1.45;
}

.controls-block {
  margin-top: 26px;
  padding-top: 20px;
  border-top: 1px solid var(--line);
}

.filters-heading {
  align-items: flex-start;
}

.filters-heading > div:first-child span {
  display: block;
  margin-top: 4px;
}

.history-actions {
  display: flex;
  align-items: center;
  gap: 5px;
}

.history-actions button {
  height: 34px;
  min-width: 34px;
  padding: 0;
  display: grid;
  place-items: center;
  border: 1px solid #d9ddd9;
  border-radius: 9px;
  color: #4c5751;
  background: #ffffff;
  cursor: pointer;
}

.history-actions button:disabled {
  color: #bdc2bf;
  background: #f1f2ef;
  cursor: not-allowed;
}

.history-actions button svg {
  width: 15px;
  height: 15px;
}

.history-actions .reset-button {
  padding: 0 9px;
  color: #657069;
  font-size: 10px;
  font-weight: 650;
}

.global-control {
  margin-top: 16px;
  padding: 13px 14px 14px;
  border: 1px solid #dde2dd;
  border-radius: 13px;
  background: #ffffff;
}

.global-control > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.global-control label {
  color: #47534d;
  font-size: 10px;
  font-weight: 650;
}

.global-control output {
  color: var(--sage-deep);
  font-size: 12px;
  font-weight: 750;
}

.global-control input,
.filter-item input {
  width: 100%;
  height: 22px;
  margin: 7px 0 -4px;
  appearance: none;
  background: transparent;
  cursor: pointer;
}

.global-control input::-webkit-slider-runnable-track,
.filter-item input::-webkit-slider-runnable-track {
  height: 4px;
  border-radius: 999px;
  background: #dfe4df;
}

.global-control input::-webkit-slider-thumb,
.filter-item input::-webkit-slider-thumb {
  width: 17px;
  height: 17px;
  margin-top: -6.5px;
  appearance: none;
  border: 3px solid white;
  border-radius: 50%;
  background: var(--sage-deep);
  box-shadow: 0 1px 5px rgba(32, 51, 41, 0.28);
}

.global-control input::-moz-range-track,
.filter-item input::-moz-range-track {
  height: 4px;
  border-radius: 999px;
  background: #dfe4df;
}

.global-control input::-moz-range-thumb,
.filter-item input::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border: 3px solid white;
  border-radius: 50%;
  background: var(--sage-deep);
  box-shadow: 0 1px 5px rgba(32, 51, 41, 0.28);
}

.empty-filters {
  min-height: 104px;
  margin-top: 10px;
  padding: 17px;
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1px dashed #cfd6d0;
  border-radius: 13px;
}

.empty-orbit {
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border: 1px solid #b8c9bf;
  border-radius: 50%;
}

.empty-orbit span {
  width: 15px;
  height: 15px;
  border: 2px solid #6d907f;
  border-radius: 50% 50% 50% 8px;
  transform: rotate(45deg);
}

.empty-filters strong {
  display: block;
  color: #49554e;
  font-size: 11px;
}

.empty-filters p {
  margin: 4px 0 0;
  color: #858e88;
  font-size: 9px;
  line-height: 1.45;
}

.filter-list {
  margin-top: 10px;
  display: grid;
  gap: 7px;
}

.filter-item {
  padding: 11px 13px 9px;
  border: 1px solid #e0e3df;
  border-radius: 13px;
  background: #ffffff;
  transition: border-color 150ms ease, box-shadow 150ms ease, background 150ms ease;
}

.filter-item.is-selected {
  border-color: #9eb7a8;
  background: #fbfdfb;
  box-shadow: 0 7px 18px rgba(47, 72, 58, 0.08);
}

.filter-item-top {
  display: flex;
  align-items: center;
  gap: 5px;
}

.filter-item-toggle {
  min-width: 0;
  flex: 1;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 0;
  color: inherit;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.filter-item-toggle:focus-visible,
.filter-remove:focus-visible {
  outline: 2px solid #759685;
  outline-offset: 3px;
}

.filter-symbol {
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 9px;
  background: #edf4f0;
}

.filter-symbol span {
  width: 12px;
  height: 12px;
  border: 1.8px solid #527463;
  border-radius: 50%;
  box-shadow: 4px -2px 0 -2px currentColor;
}

.filter-copy {
  min-width: 0;
  flex: 1;
}

.filter-copy span {
  display: block;
  color: #929995;
  font-size: 8px;
  font-weight: 750;
  letter-spacing: 0.04em;
}

.filter-copy strong {
  display: block;
  margin-top: 2px;
  overflow: hidden;
  color: #3d4942;
  font-size: 10px;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-copy .filter-waiting {
  display: block;
  margin-top: 3px;
  color: #a06f35;
  font-size: 8px;
  font-weight: 650;
  letter-spacing: 0;
}

.filter-item output {
  color: #53635b;
  font-size: 10px;
  font-weight: 750;
}

.filter-chevron {
  flex: 0 0 15px;
  width: 15px;
  height: 15px;
  color: #8b9690;
  transition: transform 150ms ease;
}

.filter-item.is-selected .filter-chevron {
  transform: rotate(180deg);
}

.filter-remove {
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  padding: 0;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 9px;
  color: #929b96;
  background: transparent;
  cursor: pointer;
  transition: color 150ms ease, background 150ms ease;
}

.filter-remove:hover {
  color: #9b4f4b;
  background: #f8eae8;
}

.filter-remove svg {
  width: 15px;
  height: 15px;
}

.filter-item-details {
  margin-top: 10px;
  padding: 10px 2px 2px;
  border-top: 1px solid #e7ebe7;
}

.filter-item-details > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.filter-item-details label {
  color: #68736d;
  font-size: 9px;
  font-weight: 650;
}

.filter-item.tone-apricot .filter-symbol {
  background: #faeee5;
}

.filter-item.tone-apricot .filter-symbol span {
  border-color: #bf7f58;
  color: #d99b73;
}

.filter-item.tone-lilac .filter-symbol {
  background: #f0edf4;
}

.filter-item.tone-lilac .filter-symbol span {
  border-color: #76698c;
  color: #9d91af;
}

.filter-item.tone-sand .filter-symbol {
  background: #f6f0e5;
}

.filter-item.tone-sand .filter-symbol span {
  border-color: #94774b;
  color: #b69969;
}

.plan-block {
  margin-top: 24px;
  overflow: hidden;
  border: 1px solid #d9dfda;
  border-radius: 14px;
  background: #f0f2ef;
}

.plan-toggle {
  width: 100%;
  min-height: 58px;
  padding: 0 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  border: 0;
  color: #3b4740;
  background: transparent;
  cursor: pointer;
}

.plan-toggle > div {
  display: flex;
  align-items: center;
  gap: 9px;
  text-align: left;
}

.code-mark {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 9px;
  color: #dceee3;
  background: #294a3b;
  font-family: "SFMono-Regular", Consolas, monospace;
  font-size: 11px;
  font-weight: 700;
}

.plan-toggle strong,
.plan-toggle small {
  display: block;
}

.plan-toggle strong {
  font-size: 11px;
}

.plan-toggle small {
  margin-top: 2px;
  color: #8a928d;
  font-size: 8px;
  letter-spacing: 0.04em;
}

.plan-status > span {
  padding: 4px 7px;
  border-radius: 999px;
  color: #3f6854;
  background: #dcebe2;
  font-family: "SFMono-Regular", Consolas, monospace;
  font-size: 8px;
  font-weight: 700;
}

.plan-status svg {
  width: 16px;
  height: 16px;
  color: #7a837d;
  transition: transform 180ms ease;
}

.plan-status svg.rotated {
  transform: rotate(180deg);
}

.plan-body {
  padding: 5px 13px 13px;
  border-top: 1px solid #d9dfda;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
}

.plan-row {
  min-height: 26px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  color: #667169;
  font-size: 8.5px;
}

.plan-row span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-row code {
  color: #355b48;
  font-size: 8.5px;
}

.plan-row code.reframed {
  color: #a85d35;
}

.plan-row code.openai {
  color: #24704d;
  font-weight: 750;
}

.plan-row code.fallback {
  color: #a85d35;
}

.plan-row.parameter {
  padding-left: 10px;
}

.plan-divider {
  height: 26px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #89918c;
  font-size: 8px;
}

.plan-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #d8ddd9;
}

.plan-empty {
  margin: 3px 0 0 10px;
  color: #8b938e;
  font-size: 8px;
}

.panel-footer {
  margin-top: 18px;
  margin-bottom: 24px;
  padding-top: 16px;
  display: flex;
  align-items: flex-start;
  gap: 9px;
  border-top: 1px solid var(--line);
  color: #8a928d;
}

.panel-footer svg {
  flex: 0 0 15px;
  width: 15px;
  height: 15px;
  color: #6e8d7d;
}

.panel-footer p {
  margin: -1px 0 0;
  font-size: 8.5px;
  line-height: 1.5;
}

.capture-backdrop {
  position: fixed;
  z-index: 80;
  inset: 0;
  padding: 30px;
  display: grid;
  place-items: center;
  background: rgba(17, 24, 20, 0.68);
  backdrop-filter: blur(12px);
}

.capture-dialog {
  position: relative;
  width: min(880px, 100%);
  max-height: calc(100vh - 60px);
  display: grid;
  grid-template-columns: minmax(320px, 0.95fr) minmax(340px, 1.05fr);
  overflow: hidden;
  border-radius: 24px;
  background: #f8f8f4;
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.28);
}

.capture-close {
  position: absolute;
  z-index: 3;
  top: 14px;
  right: 14px;
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border: 1px solid #dde1dc;
  border-radius: 50%;
  color: #4e5953;
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
}

.capture-close svg {
  width: 18px;
  height: 18px;
}

.capture-preview {
  position: relative;
  min-height: 570px;
  overflow: hidden;
  background: #d4d0c8;
}

.capture-preview img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.capture-preview .mood-tint,
.capture-preview .film-grain {
  z-index: 1;
}

.capture-preview > span {
  position: absolute;
  z-index: 2;
  left: 18px;
  bottom: 18px;
  padding: 7px 9px;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(25, 33, 28, 0.35);
  font-size: 8px;
  font-weight: 750;
  letter-spacing: 0.12em;
}

.capture-copy {
  padding: 70px 48px 42px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.capture-copy h2 {
  margin: 12px 0 10px;
  font-size: 28px;
  line-height: 1.15;
  font-weight: 720;
  letter-spacing: -0.05em;
}

.capture-copy > p {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.6;
}

.capture-summary {
  margin-top: 24px;
  padding: 14px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid #dfe2dd;
  border-bottom: 1px solid #dfe2dd;
}

.capture-summary span {
  padding: 6px 9px;
  border-radius: 999px;
  color: #4f5b54;
  background: #eceeea;
  font-size: 9px;
  font-weight: 650;
}

.capture-actions {
  margin-top: 26px;
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 8px;
}

.capture-actions button {
  min-height: 46px;
  padding: 0 14px;
  border-radius: 11px;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
}

.capture-actions .secondary {
  border: 1px solid #d6dbd6;
  color: #4e5953;
  background: white;
}

.capture-actions .primary {
  border: 1px solid #244b3a;
  color: white;
  background: #244b3a;
}

.capture-actions .icon-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.capture-actions .icon-action svg {
  width: 15px;
  height: 15px;
}

.face-map-backdrop {
  position: fixed;
  z-index: 90;
  inset: 0;
  padding: 24px;
  display: grid;
  place-items: center;
  background: rgba(15, 22, 18, 0.76);
  backdrop-filter: blur(14px);
}

.face-map-dialog {
  width: min(1120px, 100%);
  max-height: calc(100vh - 48px);
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 24px;
  background: #f7f7f3;
  box-shadow: 0 32px 100px rgba(0, 0, 0, 0.38);
}

.face-map-dialog-header {
  padding: 22px 24px 18px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  border-bottom: 1px solid #dfe3de;
}

.face-map-dialog-header h2 {
  margin: 7px 0 5px;
  color: #1d2922;
  font-size: 24px;
  line-height: 1.15;
  font-weight: 730;
  letter-spacing: -0.045em;
}

.face-map-dialog-header p {
  margin: 0;
  color: #69736d;
  font-size: 11px;
}

.face-map-dialog-header > button {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border: 1px solid #d9ded9;
  border-radius: 50%;
  color: #536059;
  background: white;
  cursor: pointer;
}

.face-map-dialog-header > button svg {
  width: 17px;
  height: 17px;
}

.face-map-calibration-layout {
  min-height: 0;
  padding: 20px 24px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  align-items: center;
  gap: 24px;
  overflow-y: auto;
}

.face-map-calibration-stage {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 18px;
  background: #17211c;
  box-shadow: 0 18px 42px rgba(24, 34, 29, 0.2);
  user-select: none;
  touch-action: none;
}

.face-map-calibration-stage > img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: fill;
  pointer-events: none;
}

.face-map-guide-layer {
  position: absolute;
  z-index: 1;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.face-map-guide-layer polyline {
  fill: none;
  stroke: rgba(255, 255, 255, 0.62);
  stroke-width: 1.15;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 3 2;
}

.face-map-guide-layer polyline[data-guide=lips] {
  fill: rgba(239, 180, 187, 0.1);
  stroke: rgba(246, 191, 201, 0.84);
}

.face-map-guide-layer polyline[data-guide=jaw] {
  stroke: rgba(189, 224, 205, 0.82);
}

.face-map-handle {
  --handle-color: #d8c9f2;
  position: absolute;
  z-index: 2;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 2px solid rgba(27, 37, 32, 0.86);
  border-radius: 50%;
  color: #223029;
  background: var(--handle-color);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.72), 0 4px 12px rgba(0, 0, 0, 0.24);
  transform: translate(-50%, -50%);
  cursor: grab;
  touch-action: none;
  transition: width 120ms ease, height 120ms ease, box-shadow 120ms ease;
}

.face-map-handle.region-nose {
  --handle-color: #efc29e;
}

.face-map-handle.region-lips {
  --handle-color: #efb4bb;
}

.face-map-handle.region-jaw {
  --handle-color: #bde0cd;
}

.face-map-handle:hover,
.face-map-handle:focus-visible,
.face-map-handle.is-dragging {
  z-index: 4;
  width: 22px;
  height: 22px;
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.86), 0 5px 18px rgba(0, 0, 0, 0.3);
}

.face-map-handle.is-dragging {
  cursor: grabbing;
}

.face-map-handle > span {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 9px);
  width: max-content;
  max-width: 120px;
  padding: 5px 7px;
  border-radius: 7px;
  color: white;
  background: rgba(25, 35, 29, 0.88);
  font-size: 8px;
  font-weight: 650;
  opacity: 0;
  pointer-events: none;
  transform: translateX(-50%) translateY(3px);
  transition: opacity 120ms ease, transform 120ms ease;
}

.face-map-handle:hover > span,
.face-map-handle:focus-visible > span,
.face-map-handle.is-dragging > span {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.face-map-calibration-help {
  align-self: stretch;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.step-label {
  color: #587565;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.face-map-calibration-help h3 {
  margin: 10px 0 8px;
  color: #26332c;
  font-size: 18px;
  line-height: 1.28;
  font-weight: 720;
  letter-spacing: -0.035em;
}

.face-map-calibration-help > p {
  margin: 0;
  color: #6a746e;
  font-size: 10px;
  line-height: 1.65;
}

.face-map-legend {
  margin: 20px 0 0;
  padding: 15px 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  border-top: 1px solid #dde2dd;
  border-bottom: 1px solid #dde2dd;
  list-style: none;
}

.face-map-legend li {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #56615b;
  font-size: 9px;
  font-weight: 650;
}

.face-map-legend li > span {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #d8c9f2;
  box-shadow: 0 0 0 2px white;
}

.face-map-legend .region-nose > span {
  background: #efc29e;
}

.face-map-legend .region-lips > span {
  background: #efb4bb;
}

.face-map-legend .region-jaw > span {
  background: #bde0cd;
}

.face-map-local-note {
  margin-top: 16px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 9px;
  border-radius: 12px;
  color: #446252;
  background: #e8f0eb;
}

.face-map-local-note svg {
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
}

.face-map-local-note div {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.face-map-local-note strong {
  font-size: 9px;
}

.face-map-local-note span {
  color: #6d7a73;
  font-size: 8px;
}

.face-map-dialog-actions {
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid #dfe3de;
  background: #fbfbf8;
}

.face-map-dialog-actions button {
  min-height: 44px;
  padding: 0 16px;
  border-radius: 11px;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
}

.face-map-dialog-actions .secondary {
  border: 1px solid #d6dcd7;
  color: #4f5b54;
  background: white;
}

.face-map-dialog-actions .primary {
  min-width: 132px;
  border: 1px solid #2f5845;
  color: white;
  background: #2f5845;
}

.toast {
  position: fixed;
  z-index: 100;
  left: 50%;
  bottom: 26px;
  min-height: 46px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 9px;
  transform: translateX(-50%);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 12px;
  color: white;
  background: #26362e;
  box-shadow: 0 12px 34px rgba(17, 28, 22, 0.24);
  font-size: 10px;
  font-weight: 600;
  animation: toastIn 180ms ease-out;
}

.toast svg {
  width: 16px;
  height: 16px;
  color: #c9e6d5;
}

@keyframes toastIn {
  from {
    opacity: 0;
    transform: translate(-50%, 8px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}
button:focus-visible,
input:focus-visible,
select:focus-visible {
  outline: 3px solid rgba(70, 117, 94, 0.32);
  outline-offset: 2px;
}

@media (max-width: 1120px) {
  .workspace {
    grid-template-columns: minmax(0, 1.2fr) minmax(380px, 0.8fr);
    padding-left: 16px;
    padding-right: 16px;
  }
  .mirror-message {
    max-width: 72%;
  }
  .comparison-control button {
    min-width: 64px;
  }
  .intro-block h1 {
    font-size: 27px;
  }
}
@media (max-width: 860px) {
  :host {
    height: auto;
  }
  .meeo-app {
    height: auto;
    min-height: 100vh;
    overflow: visible;
  }
  .topbar {
    position: sticky;
    z-index: 30;
    top: 0;
    height: 60px;
    padding: 0 16px;
    grid-template-columns: 1fr auto;
  }
  .topbar-center {
    display: none;
  }
  .workspace {
    height: auto;
    min-height: 0;
    padding: 10px 10px 22px;
    grid-template-columns: 1fr;
  }
  .mirror-stage {
    min-height: 68vh;
    border-radius: 20px;
  }
  .mood-panel {
    min-height: 640px;
    border-radius: 20px;
  }
  .panel-scroll {
    height: auto;
    overflow: visible;
  }
  .intro-block,
  .face-map-card,
  .starter-block,
  .conversation-block,
  .prompt-form,
  .controls-block,
  .plan-block,
  .panel-footer {
    margin-left: 18px;
    margin-right: 18px;
  }
  .capture-dialog {
    max-width: 620px;
    grid-template-columns: 1fr;
    overflow-y: auto;
  }
  .capture-preview {
    min-height: 48vh;
  }
  .capture-copy {
    padding: 34px 26px 28px;
  }
  .capture-close {
    position: fixed;
    top: 20px;
    right: 20px;
  }
  .face-map-dialog {
    max-width: 720px;
    overflow-y: auto;
  }
  .face-map-calibration-layout {
    grid-template-columns: 1fr;
    overflow: visible;
  }
  .face-map-calibration-stage {
    max-height: none;
  }
  .face-map-calibration-help {
    padding-bottom: 4px;
  }
}
@media (max-width: 520px) {
  .privacy-chip span {
    display: none;
  }
  .privacy-chip {
    width: 40px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
  .mirror-stage {
    min-height: 72vh;
  }
  .camera-state {
    width: calc(100% - 32px);
    padding: 24px 12px;
    transform: translate(-50%, -56%);
  }
  .camera-state-icon {
    width: 64px;
    height: 64px;
    border-radius: 22px;
  }
  .camera-state-icon svg {
    width: 36px;
    height: 36px;
  }
  .camera-state-kicker {
    margin-top: 16px;
  }
  .camera-state h2 {
    font-size: 22px;
  }
  .camera-state p {
    font-size: 11px;
  }
  .camera-state > button {
    min-height: 44px;
    margin-top: 16px;
  }
  .mirror-topbar {
    padding: 16px 16px 0;
  }
  .local-note {
    display: none;
  }
  .camera-picker {
    max-width: min(35vw, 136px);
    min-height: 36px;
  }
  .camera-toggle {
    min-height: 38px;
    padding: 0 10px;
    gap: 6px;
    font-size: 9px;
  }
  .camera-toggle svg {
    width: 16px;
    height: 16px;
  }
  .preview-label {
    top: 65px;
    left: 16px;
  }
  .face-spike-panel {
    top: 110px;
    right: 16px;
    width: min(268px, 100% - 32px);
    padding: 11px 12px 10px;
  }
  .face-spike-panel > p {
    font-size: 9px;
  }
  .mirror-message {
    left: 16px;
    bottom: 106px;
    max-width: calc(100% - 32px);
  }
  .mirror-controls {
    left: 16px;
    right: 16px;
    bottom: 18px;
  }
  .comparison-control button {
    min-width: 52px;
    min-height: 38px;
    padding: 4px 8px;
  }
  .comparison-control {
    padding: 4px;
    grid-template-columns: auto;
  }
  .comparison-label {
    display: none;
  }
  .comparison-control button small {
    display: none;
  }
  .comparison-control button strong {
    font-size: 10px;
  }
  .shutter {
    width: 62px;
    height: 62px;
  }
  .shutter-inner {
    width: 47px;
    height: 47px;
  }
  .intensity-readout span {
    display: none;
  }
  .intensity-readout strong {
    font-size: 14px;
  }
  .prompt-grid {
    grid-template-columns: 1fr;
  }
  .capture-backdrop {
    padding: 12px;
    align-items: start;
    overflow-y: auto;
  }
  .capture-dialog {
    max-height: none;
  }
  .capture-preview {
    min-height: 55vh;
  }
  .capture-copy h2 {
    font-size: 24px;
  }
  .capture-actions {
    grid-template-columns: 1fr 1fr;
  }
  .capture-actions .primary {
    grid-column: 1/-1;
  }
  .face-map-backdrop {
    padding: 10px;
    align-items: start;
    overflow-y: auto;
  }
  .face-map-dialog {
    max-height: none;
    border-radius: 18px;
  }
  .face-map-dialog-header {
    padding: 18px 16px 15px;
  }
  .face-map-dialog-header h2 {
    padding-right: 12px;
    font-size: 20px;
  }
  .face-map-dialog-header p {
    font-size: 10px;
    line-height: 1.5;
  }
  .face-map-calibration-layout {
    padding: 14px 12px;
    gap: 16px;
  }
  .face-map-calibration-stage {
    border-radius: 13px;
  }
  .face-map-handle {
    width: 16px;
    height: 16px;
  }
  .face-map-dialog-actions {
    padding: 13px 12px;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .face-map-dialog-actions .primary {
    grid-column: 1/-1;
  }
}
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}`],
})
export class PageHomeComponent implements OnInit, OnDestroy {
    constructor(@Inject( Service) public service: Service) { }

    @ViewChild('cameraVideo') public cameraVideo?: ElementRef<HTMLVideoElement>;
    @ViewChild('captureCanvas') public captureCanvas?: ElementRef<HTMLCanvasElement>;
    @ViewChild('landmarkCanvas') public landmarkCanvas?: ElementRef<HTMLCanvasElement>;
    @ViewChild('faceCalibrationStage') public faceCalibrationStage?: ElementRef<HTMLElement>;

    public codeMark: string = '{ }';
    public cameraState: CameraState = 'idle';
    public cameraErrorMessage: string = '';
    public cameraDevices: CameraDeviceOption[] = [];
    public selectedCameraId: string = '';
    public capturedFrame: string = '';
    public capturedFilterCount: number = 0;
    public capturedIntensity: number = 0;
    public capturedModeLabel: string = '원본';
    public prompt: string = '';
    public lastUserMessage: string = '';
    public assistantMessage: string = '원본 그대로 시작했어요. 오늘 원하는 빛이나 카메라 무드를 말해보세요.';
    public policyDecision: string = 'allow';
    public operation: string = 'reset';
    public planId: string = 'plan_original';
    public globalIntensity: number = 60;
    public filters: any[] = [];
    public selectedFilterId: string = '';
    public undoStack: any[] = [];
    public redoStack: any[] = [];
    public adjustmentStart: any = null;
    public showOriginal: boolean = false;
    public isInterpreting: boolean = false;
    public requestToken: number = 0;
    public safeOptions: string[] = [];
    public planOpen: boolean = true;
    public captured: boolean = false;
    public captureFlash: boolean = false;
    public isCapturing: boolean = false;
    public toastMessage: string = '';
    public readonly filterPlanEndpoint: string = FILTER_PLAN_API.endpoint;
    public readonly filterPlanSchemaVersion: string = FILTER_PLAN_SCHEMA_VERSION;
    public filterPlanSource: string = 'not_requested';
    public filterPlanModel: string = '';
    public filterPlanFallbackReason: string = '';
    public faceSpikeEnabled: boolean = false;
    public faceTrackerState: FaceTrackerState = 'disabled';
    public faceTrackerFps: number = 0;
    public faceCameraFps: number = 0;
    public faceOverlayFps: number = 0;
    public faceInferenceMs: number = 0;
    public faceInputPrepMs: number = 0;
    public facePostMessageMs: number = 0;
    public faceAverageLatencyMs: number = 0;
    public faceOverlayMs: number = 0;
    public faceLoadTimeMs: number = 0;
    public faceWasmLoadTimeMs: number = 0;
    public faceModelLoadTimeMs: number = 0;
    public faceCount: number = 0;
    public faceTargetFps: number = FACE_LANDMARK_SPIKE.desktopTargetFps;
    public faceEffectiveTargetFps: number = FACE_LANDMARK_SPIKE.desktopTargetFps;
    public faceInferenceWidth: number = 0;
    public faceInferenceHeight: number = 0;
    public faceDelegate: FaceDelegate = 'unknown';
    public faceBackend: FaceInferenceBackend = 'main-thread';
    public faceInitializationState: FaceInitializationState = 'idle';
    public faceInitializationElapsedMs: number = 0;
    public faceAssetLoadTimeMs: number = 0;
    public faceFallbackReason: string = 'none';
    public faceFailureReason: string = '';
    public faceTrackingDisabledByUser: boolean = false;
    public faceCalibrationOpen: boolean = false;
    public faceCalibrationFrame: string = '';
    public faceCalibrationPoints: FaceMapPoint[] = [];
    public faceMap: FaceMapState | null = null;
    public faceMapStale: boolean = false;
    public showFaceRecognitionPoints: boolean = false;
    public draggingFacePointId: string = '';
    public readonly faceMapGuides = FACE_MAP_GUIDES;
    public readonly faceAssetSource: string = 'same-origin';
    public readonly faceAssetRoot: string = FACE_LANDMARK_SPIKE.assetRoot;
    private mediaStream: MediaStream | null = null;
    private cameraPermission: PermissionStatus | null = null;
    private cameraDeviceChangeListener: (() => void) | null = null;
    private cameraRecoveryInProgress: boolean = false;
    private destroyed: boolean = false;
    private faceIsCoarseDevice: boolean = false;
    private faceLandmarker: FaceLandmarker | null = null;
    private faceWorker: Worker | null = null;
    private faceWorkerReady: boolean = false;
    private faceWorkerBusy: boolean = false;
    private faceWorkerRequestId: number = 0;
    private faceWorkerInitializationTimeout: ReturnType<typeof setTimeout> | null = null;
    private faceWorkerInitializationResolve: (() => void) | null = null;
    private faceWorkerInitializationReject: ((error: Error) => void) | null = null;
    private faceWorkerFallbackInProgress: boolean = false;
    private faceAssetAbortController: AbortController | null = null;
    private faceInitializationStartedAt: number = 0;
    private faceInitializationTicker: ReturnType<typeof setInterval> | null = null;
    private faceVideoFrameCallback: number | null = null;
    private faceFallbackAnimationFrame: number | null = null;
    private faceOverlayAnimationFrame: number | null = null;
    private faceInferenceCanvas: HTMLCanvasElement | null = null;
    private faceInferenceContext: CanvasRenderingContext2D | null = null;
    private facePatchCanvas: HTMLCanvasElement | null = null;
    private faceTrackerGeneration: number = 0;
    private faceLastVideoTime: number = -1;
    private faceLastDetectionAt: number = 0;
    private faceTrackingStartedAt: number = 0;
    private faceLastUiUpdateAt: number = 0;
    private faceLastOverlayAt: number = 0;
    private faceLastPoseCheckAt: number = 0;
    private faceRotationOutsideRange: boolean = false;
    private faceLastPresentedFrames: number | null = null;
    private faceLastMediaTime: number | null = null;
    private faceTargetLandmarks: any[] | null = null;
    private faceSmoothedLandmarks: any[] | null = null;
    private faceOverlayAllowsEffects: boolean = true;
    private faceOverlayOpacity: number = 0;
    private faceOverlayTargetOpacity: number = 0;
    private faceDetectionTimes: number[] = [];
    private faceCameraFpsSamples: number[] = [];
    private faceCameraFrameTimes: number[] = [];
    private faceOverlayTimes: number[] = [];
    private faceInferenceDurations: number[] = [];
    private faceInputPrepDurations: number[] = [];
    private facePostMessageDurations: number[] = [];
    private faceLatencyDurations: number[] = [];
    private faceOverlayDurations: number[] = [];
    private faceCalibrationAutoPoints: FaceMapPoint[] = [];
    private faceCalibrationFrameWidth: number = 0;
    private faceCalibrationFrameHeight: number = 0;
    private facePlanWaitingForMap: boolean = false;
    public promptExamples: string[] = [
        '눈 키워줘',
        '입술 도톰하게',
        '코 높여줘',
        '턱선 갸름하게',
        '하관 줄여줘',
        'V라인',
        '눈썹 살짝 올려줘'
    ];

    public async ngOnInit() {
        await this.service.init();
        this.faceSpikeEnabled = this.isFaceSpikeRequested();
        this.restoreFaceMapFromSession();
        this.faceTrackerState = 'camera_off';
        this.faceIsCoarseDevice = this.isCoarseMobileDevice();
        this.faceTargetFps = this.faceIsCoarseDevice
            ? FACE_LANDMARK_SPIKE.mobileTargetFps
            : FACE_LANDMARK_SPIKE.desktopTargetFps;
        this.faceEffectiveTargetFps = this.faceTargetFps;
        if (!this.cameraSupported) {
            this.cameraState = 'unsupported';
        } else {
            this.bindCameraDeviceChanges();
            await this.refreshCameraDevices();
            await this.syncCameraPermission();
        }
        await this.service.render();
    }

    public ngOnDestroy() {
        this.destroyed = true;
        if (this.cameraPermission) this.cameraPermission.onchange = null;
        if (this.cameraDeviceChangeListener) {
            navigator.mediaDevices.removeEventListener('devicechange', this.cameraDeviceChangeListener);
            this.cameraDeviceChangeListener = null;
        }
        this.stopMediaStream();
        this.disposeFaceInferenceBackend();
    }

    public get cameraSupported(): boolean {
        return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
    }

    public get cameraIsActive(): boolean {
        return this.cameraState === 'active';
    }

    public get faceTrackerStatusLabel(): string {
        switch (this.faceInitializationState) {
            case 'asset_loading': return 'Asset loading';
            case 'worker_initializing': return 'Worker initializing';
            case 'fallback': return 'Fallback 전환 중';
            case 'failed': return '초기화 실패';
            case 'cancelled': return '얼굴 추적 비활성';
        }

        switch (this.faceTrackerState) {
            case 'camera_off': return '카메라 대기';
            case 'loading': return '모델 로딩';
            case 'ready': return 'Model ready';
            case 'tracking': return '얼굴 1명 추적';
            case 'no_face': return '얼굴 미검출';
            case 'multiple_faces': return '여러 얼굴 감지';
            case 'rotated': return '고개 회전 감지';
            case 'degraded': return '성능 제한 모드';
            case 'error': return '추적 오류';
            default: return '비활성';
        }
    }

    public get faceTrackerStatusDetail(): string {
        switch (this.faceInitializationState) {
            case 'asset_loading':
                return 'same-origin MediaPipe loader와 모델 자산을 확인하고 있어요.';
            case 'worker_initializing':
                return '로컬 Worker에서 WASM과 Face Landmarker를 준비하고 있어요.';
            case 'fallback':
                return 'Worker 대신 main-thread 호환 모드로 안전하게 전환하고 있어요.';
            case 'failed':
                return '초기화에 실패했어요. 얼굴 추적을 다시 켜 재시도할 수 있어요.';
            case 'cancelled':
                return '카메라는 유지됩니다. 원할 때 얼굴 추적만 다시 켤 수 있어요.';
        }

        switch (this.faceTrackerState) {
            case 'camera_off': return '카메라를 켜면 브라우저 로컬 추적을 시작해요.';
            case 'loading': return 'MediaPipe WASM과 모델을 불러오고 있어요.';
            case 'ready': return '첫 카메라 프레임을 기다리고 있어요.';
            case 'tracking': return '눈·코·입·턱 기준점과 선택한 효과를 표시해요.';
            case 'no_face': return '화면 중앙에 얼굴이 보이도록 위치를 조정해주세요.';
            case 'multiple_faces': return '단일 사용자만 지원해 얼굴 부착 효과를 멈췄어요.';
            case 'rotated': return '정면 기준을 벗어나 얼굴 부착 효과를 잠시 줄였어요.';
            case 'degraded': return '추적 빈도를 낮춰 카메라 반응성을 우선하고 있어요.';
            case 'error': return '전체 화면 필터는 유지되며 얼굴 부착 효과만 멈췄어요.';
            default: return 'URL에 ?face-spike=1을 추가하면 개발 모드를 켤 수 있어요.';
        }
    }

    public get faceTrackerStateClass(): string {
        const state = ['fallback', 'failed', 'cancelled'].includes(this.faceInitializationState)
            ? this.faceInitializationState
            : this.faceTrackerState;
        return 'state-' + state.replace('_', '-');
    }

    public get faceInitializationPending(): boolean {
        return ['asset_loading', 'worker_initializing', 'fallback'].includes(this.faceInitializationState);
    }

    public get faceInitializationStatusLabel(): string {
        switch (this.faceInitializationState) {
            case 'asset_loading': return 'ASSET LOADING';
            case 'worker_initializing': return 'WORKER INITIALIZING';
            case 'model_ready': return 'MODEL READY';
            case 'fallback': return 'FALLBACK';
            case 'failed': return 'FAILED';
            case 'cancelled': return 'DISABLED';
            default: return 'IDLE';
        }
    }

    public get filterPlanStatusLabel(): string {
        if (this.isInterpreting) return 'WIZ API 요청 중';
        if (this.filterPlanSource === 'not_requested') return 'WIZ API 대기';
        if (this.filterPlanSource === 'openai') {
            return this.filterPlanModel
                ? this.filterPlanSource + ' · ' + this.filterPlanModel
                : this.filterPlanSource;
        }
        if (this.filterPlanSource === 'local_facemap') return '브라우저 FaceMap';
        if (this.filterPlanSource === 'mock' || this.filterPlanSource === 'client_mock') {
            return 'mock fallback';
        }
        return this.filterPlanSource;
    }

    public get cameraStatusLabel(): string {
        switch (this.cameraState) {
            case 'active': return 'LIVE CAMERA';
            case 'requesting': return 'CONNECTING';
            case 'denied': return 'PERMISSION DENIED';
            case 'unavailable': return 'NO CAMERA';
            case 'unsupported': return 'UNSUPPORTED';
            case 'error': return 'CAMERA ERROR';
            default: return 'CAMERA OFF';
        }
    }

    public get cameraStateTitle(): string {
        switch (this.cameraState) {
            case 'requesting': return '카메라 연결을 기다리고 있어요';
            case 'off': return '카메라를 껐어요';
            case 'denied': return '카메라 권한이 필요해요';
            case 'unavailable': return '사용할 수 있는 카메라가 없어요';
            case 'unsupported': return '이 브라우저에서는 카메라를 열 수 없어요';
            case 'error': return '카메라를 연결하지 못했어요';
            default: return '내 얼굴을 비추는 미러를 켜보세요';
        }
    }

    public get cameraStateDescription(): string {
        switch (this.cameraState) {
            case 'requesting': return '브라우저의 카메라 사용 요청을 확인해주세요.';
            case 'off': return '카메라 track과 얼굴 인식 리소스를 모두 종료했어요. 적용 중인 필터는 그대로 유지돼요.';
            case 'denied': return '브라우저 주소창의 권한 설정에서 카메라를 허용한 뒤 다시 시도해주세요.';
            case 'unavailable': return this.cameraErrorMessage
                || '연결된 카메라를 찾지 못했어요. 기기 연결 상태를 확인해주세요.';
            case 'unsupported': return '최신 Chrome, Safari, Edge 등 카메라를 지원하는 브라우저의 HTTPS 환경에서 이용해주세요.';
            case 'error': return this.cameraErrorMessage || '다른 앱이 카메라를 사용 중인지 확인하고 다시 시도해주세요.';
            default: return this.cameraErrorMessage || '영상은 서버로 전송되거나 자동으로 저장되지 않아요.';
        }
    }

    public get cameraActionLabel(): string {
        if (this.cameraState === 'denied' || this.cameraState === 'error') return '다시 요청하기';
        if (this.cameraState === 'unavailable') return '다시 확인하기';
        if (this.cameraState === 'unsupported') return '카메라 사용 불가';
        if (this.cameraState === 'off') return '카메라 다시 켜기';
        return '카메라 켜기';
    }

    public get cameraToggleLabel(): string {
        if (this.cameraIsActive) return '카메라 끄기';
        if (this.cameraState === 'off') return '카메라 다시 켜기';
        return '카메라 켜기';
    }

    public get faceMapReady(): boolean {
        return !!this.faceMap && !this.faceMapStale;
    }

    public get faceRecognitionPointsVisible(): boolean {
        if (this.showOriginal) return false;
        return this.faceMapReady ? this.showFaceRecognitionPoints : this.faceSpikeEnabled;
    }

    public get faceEffectsWaiting(): boolean {
        return this.facePlanWaitingForMap && !this.faceMapReady;
    }

    public get faceMapStatusLabel(): string {
        if (this.faceMapReady) return '인식 완료';
        if (this.faceMapStale) return this.cameraIsActive ? '다시 인식 필요' : '카메라 꺼짐 · 재인식 필요';
        if (this.faceEffectsWaiting) return '표현 적용 대기';
        if (!this.cameraIsActive) return '카메라 대기';
        if (this.faceInitializationPending) return '모델 준비 중';
        if (this.faceInitializationState === 'failed') return '인식 준비 실패';
        if (this.faceTrackerState === 'multiple_faces') return '한 명만 필요';
        if (this.faceTrackerState === 'no_face') return '얼굴을 찾는 중';
        if (this.faceTrackerState === 'rotated') return '정면 확인 필요';
        if (['tracking', 'degraded'].includes(this.faceTrackerState)) return '보정 준비 완료';
        return '준비 중';
    }

    public get faceMapStatusDetail(): string {
        if (this.faceMapReady) {
            return '인식 포인트는 기본 촬영 화면에서 숨겨져요. 필요할 때만 다시 보거나 수정할 수 있어요.';
        }
        if (this.faceMapStale) {
            return this.cameraIsActive
                ? '카메라가 다시 켜졌어요. 기존 필터는 유지되지만 얼굴 기준점은 현재 얼굴을 다시 인식한 뒤 적용돼요.'
                : '카메라가 꺼져 기존 FaceMap을 사용하지 않아요. 카메라를 다시 켠 뒤 얼굴을 다시 인식해주세요.';
        }
        if (this.faceEffectsWaiting) {
            return 'FilterPlan과 슬라이더는 준비됐어요. 실제 얼굴 변화는 FaceMap 인식을 완료한 뒤 로컬에서 적용돼요.';
        }
        if (!this.cameraIsActive) {
            return '카메라를 켠 뒤 정면을 비추고 얼굴 인식을 시작해주세요.';
        }
        if (this.faceInitializationPending || this.faceTrackerState === 'loading') {
            return '브라우저 안에서 MediaPipe 얼굴 모델을 준비하고 있어요.';
        }
        if (this.faceTrackerState === 'multiple_faces') {
            return '한 명의 얼굴만 화면에 보이게 한 뒤 다시 시도해주세요.';
        }
        if (this.faceTrackerState === 'no_face') {
            return '얼굴 전체가 프레임 중앙에 보이도록 위치를 맞춰주세요.';
        }
        if (this.faceTrackerState === 'rotated') {
            return '눈과 코 기준점을 정확히 잡을 수 있도록 카메라를 정면으로 봐주세요.';
        }
        if (this.faceInitializationState === 'failed') {
            return '로컬 얼굴 모델을 준비하지 못했어요. 다시 시도할 수 있어요.';
        }
        return '정지 프레임에서 자동 기준점을 확인하고 직접 드래그해 보정할 수 있어요.';
    }

    public get faceRecognitionActionLabel(): string {
        if (!this.cameraIsActive) return '카메라를 먼저 켜주세요';
        if (this.faceInitializationPending) return '인식 모델 준비 중';
        if (this.faceMapStale) return '얼굴 다시 인식';
        return '얼굴 인식 시작';
    }

    public get faceCalibrationAspectRatio(): string {
        const width = this.faceCalibrationFrameWidth || 16;
        const height = this.faceCalibrationFrameHeight || 9;
        return width + ' / ' + height;
    }

    private isFaceSpikeRequested(): boolean {
        if (typeof window === 'undefined') return false;
        const value = new URLSearchParams(window.location.search).get(FACE_LANDMARK_SPIKE.queryParameter);
        return ['1', 'true', 'on'].includes(String(value || '').toLowerCase());
    }

    private isCoarseMobileDevice(): boolean {
        if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
        return window.matchMedia?.('(max-width: 860px), (pointer: coarse)').matches
            || (navigator.hardwareConcurrency > 0 && navigator.hardwareConcurrency <= 4);
    }

    private bindCameraDeviceChanges() {
        if (!this.cameraSupported || !navigator.mediaDevices.addEventListener) return;

        this.cameraDeviceChangeListener = () => {
            this.handleCameraDevicesChanged().catch(() => undefined);
        };
        navigator.mediaDevices.addEventListener('devicechange', this.cameraDeviceChangeListener);
    }

    private async refreshCameraDevices(preferredDeviceId: string = this.selectedCameraId): Promise<CameraDeviceOption[]> {
        if (!this.cameraSupported || !navigator.mediaDevices.enumerateDevices) {
            this.cameraDevices = [];
            return [];
        }

        try {
            const devices = (await navigator.mediaDevices.enumerateDevices())
                .filter((device) => device.kind === 'videoinput')
                .map((device, index) => ({
                    deviceId: device.deviceId,
                    label: device.label.trim() || `카메라 ${index + 1}`
                }));
            this.cameraDevices = devices;

            const activeDeviceId = this.mediaStream?.getVideoTracks()[0]?.getSettings().deviceId || '';
            const nextDeviceId = activeDeviceId || preferredDeviceId;
            this.selectedCameraId = devices.some((device) => device.deviceId === nextDeviceId)
                ? nextDeviceId
                : '';
            return devices;
        } catch (error) {
            return this.cameraDevices;
        }
    }

    private async handleCameraDevicesChanged() {
        if (this.destroyed) return;

        const previousDeviceCount = this.cameraDevices.length;
        const activeStream = this.mediaStream;
        const activeDeviceId = activeStream?.getVideoTracks()[0]?.getSettings().deviceId || '';
        const devices = await this.refreshCameraDevices(activeDeviceId || this.selectedCameraId);
        if (this.destroyed) return;

        if (
            activeStream
            && this.mediaStream === activeStream
            && activeDeviceId
            && !devices.some((device) => device.deviceId === activeDeviceId)
        ) {
            await this.recoverCameraAfterDisconnect(activeStream, activeDeviceId);
            return;
        }

        if (this.cameraState === 'unavailable' && devices.length > 0) {
            this.cameraState = 'idle';
            this.cameraErrorMessage = '새로 연결된 카메라를 찾았어요. 카메라 켜기를 눌러주세요.';
        } else if (
            this.cameraState !== 'off'
            && !activeStream
            && previousDeviceCount > 0
            && devices.length === 0
        ) {
            this.cameraState = 'unavailable';
            this.cameraErrorMessage = '연결된 카메라가 해제됐어요. 내장 카메라나 외장 웹캠을 연결해주세요.';
        }

        await this.service.render();
    }

    private async syncCameraPermission() {
        if (!this.cameraSupported || !navigator.permissions?.query) return;

        try {
            const permission = await navigator.permissions.query({ name: 'camera' as PermissionName });
            this.cameraPermission = permission;
            this.applyCameraPermissionState(permission.state);
            permission.onchange = async () => {
                this.applyCameraPermissionState(permission.state);
                if (!this.destroyed) await this.service.render();
            };
        } catch (error) {
            // Safari와 일부 브라우저는 camera 권한 조회를 지원하지 않는다.
            // 이 경우 사용자 동작으로 getUserMedia를 호출해 실제 상태를 판별한다.
        }
    }

    private applyCameraPermissionState(state: PermissionState) {
        if (state === 'denied') {
            const wasExplicitlyOff = this.cameraState === 'off';
            this.stopMediaStream();
            if (!wasExplicitlyOff) {
                this.cameraState = 'denied';
                this.cameraErrorMessage = '';
            }
            return;
        }

        if (state !== 'denied' && this.cameraState === 'denied') {
            this.cameraState = 'idle';
            this.cameraErrorMessage = '';
        }
    }

    public async startCamera() {
        if (this.cameraState === 'requesting' || this.cameraState === 'active') return;

        if (!this.cameraSupported) {
            this.cameraState = 'unsupported';
            await this.service.render();
            return;
        }

        this.cameraState = 'requesting';
        this.cameraErrorMessage = '';
        this.stopMediaStream();
        await this.service.render();

        try {
            const stream = await this.requestCameraStream(this.selectedCameraId);

            if (this.destroyed) {
                stream.getTracks().forEach((track) => track.stop());
                return;
            }

            const video = this.cameraVideo?.nativeElement;
            if (!video) throw new Error('카메라 프리뷰 요소를 찾지 못했어요.');

            this.mediaStream = stream;
            this.bindMediaStreamLifecycle(stream);
            video.srcObject = stream;
            await video.play();
            const activeDeviceId = stream.getVideoTracks()[0]?.getSettings().deviceId || this.selectedCameraId;
            await this.refreshCameraDevices(activeDeviceId);

            if (this.destroyed || this.mediaStream !== stream) {
                stream.getTracks().forEach((track) => track.stop());
                return;
            }

            this.cameraState = 'active';
            await this.service.render();
            await this.startFaceTracking();
        } catch (error: any) {
            this.stopMediaStream();
            this.setCameraErrorState(error);
            await this.service.render();
        }
    }

    public async changeCamera(event: Event) {
        const nextDeviceId = (event.target as HTMLSelectElement)?.value || '';
        if (nextDeviceId === this.selectedCameraId && this.cameraIsActive) return;

        this.selectedCameraId = nextDeviceId;
        if (this.cameraIsActive) {
            this.stopMediaStream();
            this.cameraState = 'idle';
        }
        this.cameraErrorMessage = '';
        await this.startCamera();
    }

    public async toggleCamera() {
        if (this.cameraIsActive) {
            this.stopMediaStream();
            this.cameraState = 'off';
            this.cameraErrorMessage = '';
            this.assistantMessage = '카메라와 얼굴 인식 리소스를 모두 종료했어요. 기존 필터는 다시 켠 뒤에도 유지돼요.';
            await this.service.render();
            return;
        }

        await this.startCamera();
    }

    private bindMediaStreamLifecycle(stream: MediaStream) {
        stream.getVideoTracks().forEach((track) => {
            track.onended = async () => {
                if (this.destroyed || this.mediaStream !== stream) return;
                await this.recoverCameraAfterDisconnect(stream, track.getSettings().deviceId || '');
            };
        });
    }

    private cameraConstraints(deviceId: string = ''): MediaStreamConstraints {
        const video: MediaTrackConstraints = {
            width: { ideal: 1280 },
            height: { ideal: 720 }
        };
        if (deviceId) video.deviceId = { exact: deviceId };
        return { video, audio: false };
    }

    private async requestCameraStream(deviceId: string): Promise<MediaStream> {
        try {
            return await navigator.mediaDevices.getUserMedia(this.cameraConstraints(deviceId));
        } catch (error: any) {
            const staleDeviceSelection = deviceId && [
                'NotFoundError',
                'DevicesNotFoundError',
                'OverconstrainedError',
                'ConstraintNotSatisfiedError'
            ].includes(error?.name || '');
            if (!staleDeviceSelection) throw error;

            this.selectedCameraId = '';
            await this.refreshCameraDevices();
            return navigator.mediaDevices.getUserMedia(this.cameraConstraints());
        }
    }

    private async recoverCameraAfterDisconnect(stream: MediaStream, disconnectedDeviceId: string) {
        if (
            this.destroyed
            || this.mediaStream !== stream
            || this.cameraRecoveryInProgress
        ) return;

        this.cameraRecoveryInProgress = true;
        this.stopMediaStream();
        this.cameraState = 'idle';

        try {
            const devices = await this.refreshCameraDevices();
            const fallback = devices.find((device) => device.deviceId !== disconnectedDeviceId);
            this.selectedCameraId = fallback?.deviceId || '';

            if (devices.length > 0) {
                await this.startCamera();
                if (this.cameraIsActive) this.showToast('연결 가능한 다른 카메라로 자동 전환했어요.');
                return;
            }

            this.cameraState = 'unavailable';
            this.cameraErrorMessage = '카메라 연결이 중단됐어요. 내장 카메라나 외장 웹캠 연결 상태를 확인해주세요.';
            await this.service.render();
        } finally {
            this.cameraRecoveryInProgress = false;
        }
    }

    private stopMediaStream() {
        const stoppedActiveCamera = !!this.mediaStream || this.cameraState === 'active';
        this.stopFaceTracking('camera_off');
        this.disposeFaceInferenceBackend();
        this.faceBackend = 'main-thread';
        this.faceWorkerFallbackInProgress = false;
        this.faceTrackerState = 'camera_off';
        this.faceInitializationState = this.faceTrackingDisabledByUser ? 'cancelled' : 'idle';

        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach((track) => {
                track.onended = null;
                track.stop();
            });
            this.mediaStream = null;
        }

        const video = this.cameraVideo?.nativeElement;
        if (video) {
            video.pause();
            video.srcObject = null;
        }

        this.isCapturing = false;
        this.captureFlash = false;
        this.faceCalibrationOpen = false;
        this.faceCalibrationFrame = '';
        this.faceCalibrationPoints = [];
        this.faceCalibrationAutoPoints = [];
        this.draggingFacePointId = '';

        if (stoppedActiveCamera) this.markFaceMapStale();
    }

    private markFaceMapStale() {
        if (this.faceMap) this.faceMapStale = true;
        this.showFaceRecognitionPoints = false;
        this.facePlanWaitingForMap = this.hasFaceFilters();
    }

    private setCameraErrorState(error: any) {
        const name = error?.name || '';

        if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
            this.cameraState = 'denied';
            this.cameraErrorMessage = '';
            return;
        }

        if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
            this.cameraState = 'unavailable';
            this.cameraErrorMessage = '';
            return;
        }

        this.cameraState = 'error';
        this.cameraErrorMessage = name === 'NotReadableError' || name === 'TrackStartError'
            ? '카메라가 다른 앱에서 사용 중일 수 있어요. 다른 앱을 닫고 다시 시도해주세요.'
            : '카메라 연결 중 문제가 생겼어요. 잠시 후 다시 시도해주세요.';
    }

    public async startFaceRecognition() {
        const video = this.cameraVideo?.nativeElement;
        const canvas = this.captureCanvas?.nativeElement;

        if (!this.cameraIsActive || !video || !canvas || video.readyState < 2) {
            this.assistantMessage = '얼굴 인식을 시작하려면 먼저 카메라를 켜주세요.';
            this.showToast('카메라를 켠 뒤 얼굴 인식을 시작해주세요.');
            await this.service.render();
            return;
        }

        if (this.faceTrackingDisabledByUser) {
            this.faceTrackingDisabledByUser = false;
            this.faceInitializationState = 'idle';
            await this.startFaceTracking();
            return;
        }

        if (this.faceInitializationState === 'failed') {
            await this.retryFaceTracking();
            this.showToast('로컬 얼굴 모델을 다시 준비하고 있어요.');
            return;
        }

        if (this.faceInitializationPending || ['loading', 'ready'].includes(this.faceTrackerState)) {
            this.assistantMessage = '브라우저 안에서 얼굴 인식 모델을 준비하고 있어요. 잠시 후 다시 눌러주세요.';
            this.showToast('얼굴 인식 모델을 준비하고 있어요.');
            await this.service.render();
            return;
        }

        if (this.faceTrackerState === 'multiple_faces') {
            this.assistantMessage = 'FaceMap은 한 사람씩 만들 수 있어요. 화면에 한 명만 보이게 해주세요.';
            this.showToast('한 명의 얼굴만 화면에 보이게 해주세요.');
            await this.service.render();
            return;
        }

        if (this.faceTrackerState === 'rotated') {
            this.assistantMessage = '정확한 기준점을 위해 카메라를 정면으로 본 뒤 다시 시작해주세요.';
            this.showToast('정면을 본 뒤 다시 시도해주세요.');
            await this.service.render();
            return;
        }

        const landmarks = this.faceTargetLandmarks;
        const hasRequiredPoints = !!landmarks && FACE_MAP_POINT_DEFINITIONS.every(
            (definition) => !!landmarks[definition.index]
        );
        if (!hasRequiredPoints) {
            this.assistantMessage = '아직 얼굴 기준점을 찾지 못했어요. 얼굴 전체가 보이도록 위치를 맞춰주세요.';
            this.showToast('얼굴 기준점을 찾는 중이에요.');
            await this.service.render();
            return;
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        if (!context) {
            this.showToast('보정용 정지 프레임을 만들지 못했어요.');
            return;
        }

        context.save();
        context.setTransform(1, 0, 0, 1, 0, 0);
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        context.restore();

        try {
            this.faceCalibrationFrame = canvas.toDataURL('image/jpeg', 0.92);
        } catch (error) {
            this.showToast('보정용 정지 프레임을 만들지 못했어요.');
            return;
        }

        this.faceCalibrationFrameWidth = canvas.width;
        this.faceCalibrationFrameHeight = canvas.height;
        this.faceCalibrationAutoPoints = FACE_MAP_POINT_DEFINITIONS.map((definition) => {
            const landmark = landmarks[definition.index];
            return {
                ...definition,
                x: this.clampNormalized(landmark.x),
                y: this.clampNormalized(landmark.y),
                z: Number(landmark.z) || 0,
                offsetX: 0,
                offsetY: 0
            };
        });
        this.faceCalibrationPoints = this.cloneFaceMapPoints(this.faceCalibrationAutoPoints);
        this.draggingFacePointId = '';
        this.showFaceRecognitionPoints = true;
        this.faceCalibrationOpen = true;
        this.assistantMessage = '자동 감지한 기준점을 정지 프레임에 표시했어요. 어긋난 점은 드래그해 맞춰주세요.';
        await this.service.render();
    }

    public async closeFaceCalibration() {
        this.faceCalibrationOpen = false;
        this.faceCalibrationFrame = '';
        this.faceCalibrationPoints = [];
        this.faceCalibrationAutoPoints = [];
        this.draggingFacePointId = '';
        if (this.faceMapReady) this.showFaceRecognitionPoints = false;
        await this.service.render();
    }

    public async resetFaceCalibrationPoints() {
        this.faceCalibrationPoints = this.cloneFaceMapPoints(this.faceCalibrationAutoPoints);
        this.draggingFacePointId = '';
        await this.service.render();
        this.showToast('자동 감지 위치로 되돌렸어요.');
    }

    public async completeFaceRecognition() {
        if (this.faceCalibrationPoints.length !== FACE_MAP_POINT_DEFINITIONS.length) {
            this.showToast('모든 얼굴 기준점을 확인한 뒤 완료해주세요.');
            return;
        }

        const autoPoints = new Map(this.faceCalibrationAutoPoints.map((point) => [point.id, point]));
        const points = this.faceCalibrationPoints.reduce((result, point) => {
            const autoPoint = autoPoints.get(point.id) || point;
            result[point.id] = {
                ...point,
                x: this.clampNormalized(point.x),
                y: this.clampNormalized(point.y),
                offsetX: this.clampFaceMapOffset(point.x - autoPoint.x),
                offsetY: this.clampFaceMapOffset(point.y - autoPoint.y)
            };
            return result;
        }, {} as Record<string, FaceMapPoint>);

        this.faceMapStale = false;
        this.faceMap = {
            version: '1.0',
            createdAt: new Date().toISOString(),
            source: 'mediapipe-local',
            frameWidth: this.faceCalibrationFrameWidth,
            frameHeight: this.faceCalibrationFrameHeight,
            points,
            regions: {
                eyes: [...FACE_MAP_REGIONS.eyes],
                eyebrows: [...FACE_MAP_REGIONS.eyebrows],
                nose: [...FACE_MAP_REGIONS.nose],
                lips: [...FACE_MAP_REGIONS.lips],
                jaw: [...FACE_MAP_REGIONS.jaw]
            }
        };
        this.persistFaceMapToSession();

        this.faceCalibrationOpen = false;
        this.faceCalibrationFrame = '';
        this.faceCalibrationPoints = [];
        this.faceCalibrationAutoPoints = [];
        this.draggingFacePointId = '';
        this.showFaceRecognitionPoints = false;
        const appliedWaitingPlan = this.facePlanWaitingForMap && this.hasFaceFilters();
        this.facePlanWaitingForMap = false;
        this.assistantMessage = appliedWaitingPlan
            ? '얼굴 인식을 완료하고 대기 중이던 FilterPlan 얼굴 표현을 로컬에서 적용했어요.'
            : '얼굴 인식을 완료했어요. 보정한 FaceMap을 현재 탭에만 저장했어요.';

        await this.service.render();
        this.showToast(appliedWaitingPlan ? 'FaceMap을 저장하고 요청한 표현을 적용했어요.' : 'FaceMap 인식을 완료했어요.');
    }

    public async toggleFaceRecognitionPoints() {
        if (!this.faceMapReady) return;
        this.showFaceRecognitionPoints = !this.showFaceRecognitionPoints;
        if (this.showFaceRecognitionPoints) this.showOriginal = false;
        await this.service.render();
        this.showToast(this.showFaceRecognitionPoints
            ? '인식 포인트를 표시했어요. 필요하면 수정 버튼을 눌러 위치를 조정하세요.'
            : '인식 포인트를 숨겼어요. 보정 결과만 확인할 수 있어요.');
    }

    public startFacePointDrag(point: FaceMapPoint, event: PointerEvent) {
        event.preventDefault();
        this.draggingFacePointId = point.id;
        const target = event.currentTarget as HTMLElement;
        if (target?.setPointerCapture) target.setPointerCapture(event.pointerId);
    }

    public moveFacePoint(event: PointerEvent) {
        if (!this.draggingFacePointId) return;
        const stage = this.faceCalibrationStage?.nativeElement;
        if (!stage) return;

        const bounds = stage.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return;
        const displayX = this.clampNormalized((event.clientX - bounds.left) / bounds.width);
        const displayY = this.clampNormalized((event.clientY - bounds.top) / bounds.height);
        this.updateFaceCalibrationPoint(this.draggingFacePointId, 1 - displayX, displayY);
    }

    public endFacePointDrag(event: PointerEvent) {
        const target = event.currentTarget as HTMLElement;
        if (target?.hasPointerCapture?.(event.pointerId)) target.releasePointerCapture(event.pointerId);
        this.draggingFacePointId = '';
    }

    public nudgeFacePoint(point: FaceMapPoint, event: KeyboardEvent) {
        const step = event.shiftKey ? 0.01 : 0.003;
        let nextX = point.x;
        let nextY = point.y;

        if (event.key === 'ArrowLeft') nextX += step;
        else if (event.key === 'ArrowRight') nextX -= step;
        else if (event.key === 'ArrowUp') nextY -= step;
        else if (event.key === 'ArrowDown') nextY += step;
        else return;

        event.preventDefault();
        this.updateFaceCalibrationPoint(point.id, nextX, nextY);
    }

    public faceMapDisplayX(point: FaceMapPoint): number {
        return (1 - this.clampNormalized(point.x)) * 100;
    }

    public faceMapDisplayY(point: FaceMapPoint): number {
        return this.clampNormalized(point.y) * 100;
    }

    public faceMapPolyline(pointIds: string[]): string {
        return pointIds
            .map((pointId) => this.faceCalibrationPoints.find((point) => point.id === pointId))
            .filter((point): point is FaceMapPoint => !!point)
            .map((point) => this.faceMapDisplayX(point).toFixed(3) + ',' + this.faceMapDisplayY(point).toFixed(3))
            .join(' ');
    }

    private updateFaceCalibrationPoint(pointId: string, x: number, y: number) {
        this.faceCalibrationPoints = this.faceCalibrationPoints.map((point) => (
            point.id === pointId
                ? { ...point, x: this.clampNormalized(x), y: this.clampNormalized(y) }
                : point
        ));
        if (!this.destroyed) void this.service.render();
    }

    private cloneFaceMapPoints(points: FaceMapPoint[]): FaceMapPoint[] {
        return points.map((point) => ({ ...point }));
    }

    private clampNormalized(value: number): number {
        const numeric = Number(value);
        if (!Number.isFinite(numeric)) return 0.5;
        return Math.max(0, Math.min(1, numeric));
    }

    private clampFaceMapOffset(value: number): number {
        const numeric = Number(value);
        if (!Number.isFinite(numeric)) return 0;
        return Math.max(-0.12, Math.min(0.12, numeric));
    }

    private persistFaceMapToSession() {
        if (typeof sessionStorage === 'undefined' || !this.faceMap) return;
        try {
            sessionStorage.setItem(FACE_MAP_STORAGE_KEY, JSON.stringify(this.faceMap));
        } catch (error) {
            // 저장 공간이 제한된 환경에서도 메모리 내 FaceMap은 현재 화면에서 유지한다.
        }
    }

    private restoreFaceMapFromSession() {
        if (typeof sessionStorage === 'undefined') return;
        try {
            const serialized = sessionStorage.getItem(FACE_MAP_STORAGE_KEY);
            if (!serialized) return;
            const candidate = JSON.parse(serialized);
            const valid = candidate?.version === '1.0'
                && candidate?.source === 'mediapipe-local'
                && candidate?.points
                && FACE_MAP_POINT_DEFINITIONS.every((definition) => {
                    const point = candidate.points[definition.id];
                    return point
                        && point.index === definition.index
                        && Number.isFinite(Number(point.x))
                        && Number.isFinite(Number(point.y))
                        && Number.isFinite(Number(point.offsetX))
                        && Number.isFinite(Number(point.offsetY));
                });
            if (!valid) {
                sessionStorage.removeItem(FACE_MAP_STORAGE_KEY);
                return;
            }
            const points = FACE_MAP_POINT_DEFINITIONS.reduce((result, definition) => {
                const point = candidate.points[definition.id];
                result[definition.id] = {
                    ...definition,
                    x: this.clampNormalized(point.x),
                    y: this.clampNormalized(point.y),
                    z: Number(point.z) || 0,
                    offsetX: this.clampFaceMapOffset(point.offsetX),
                    offsetY: this.clampFaceMapOffset(point.offsetY)
                };
                return result;
            }, {} as Record<string, FaceMapPoint>);
            this.faceMap = {
                version: '1.0',
                createdAt: typeof candidate.createdAt === 'string' ? candidate.createdAt : new Date().toISOString(),
                source: 'mediapipe-local',
                frameWidth: Math.max(1, Number(candidate.frameWidth) || 1),
                frameHeight: Math.max(1, Number(candidate.frameHeight) || 1),
                points,
                regions: {
                    eyes: [...FACE_MAP_REGIONS.eyes],
                    eyebrows: [...FACE_MAP_REGIONS.eyebrows],
                    nose: [...FACE_MAP_REGIONS.nose],
                    lips: [...FACE_MAP_REGIONS.lips],
                    jaw: [...FACE_MAP_REGIONS.jaw]
                }
            };
            this.faceMapStale = true;
        } catch (error) {
            try { sessionStorage.removeItem(FACE_MAP_STORAGE_KEY); } catch (storageError) { }
        }
    }

    private async startFaceTracking() {
        const video = this.cameraVideo?.nativeElement;
        if (!this.cameraIsActive || !video) return;
        if (this.faceTrackingDisabledByUser) {
            this.faceTrackerState = 'disabled';
            this.faceInitializationState = 'cancelled';
            await this.service.render();
            return;
        }

        const generation = ++this.faceTrackerGeneration;
        this.cancelFaceFrameLoop();
        this.clearFaceOverlay();
        this.resetFaceTrackerMetrics(!this.isFaceInferenceBackendReady());

        if (this.isFaceInferenceBackendReady()) {
            this.faceTrackingStartedAt = performance.now();
            this.faceInitializationState = 'model_ready';
            this.setFaceTrackerState('ready', 'warm_local_model_ready');
            await this.service.render();
            this.scheduleFaceFrame();
            this.scheduleFaceOverlayFrame();
            return;
        }

        this.beginFaceInitialization();
        this.setFaceTrackerState('loading', 'initializing_local_model');
        await this.service.render();

        const loadStartedAt = performance.now();

        try {
            await this.preloadFaceAssets(generation);
            await this.initializeFaceInferenceBackend(generation);

            if (this.destroyed || generation !== this.faceTrackerGeneration || !this.cameraIsActive) {
                return;
            }

            this.faceLoadTimeMs = Math.round(performance.now() - loadStartedAt);
            this.finishFaceInitialization('model_ready');
            this.faceTrackingStartedAt = performance.now();
            this.setFaceTrackerState('ready', 'local_model_ready');
            await this.service.render();
            this.scheduleFaceFrame();
            this.scheduleFaceOverlayFrame();
        } catch (error) {
            if (generation !== this.faceTrackerGeneration || this.destroyed) return;
            this.faceFailureReason = error instanceof Error ? error.message : String(error);
            this.finishFaceInitialization('failed');
            this.setFaceTrackerState('error', 'model_initialization_failed');
            this.clearFaceOverlay();
            await this.service.render();
        }
    }

    public async disableFaceTracking() {
        if (!this.faceSpikeEnabled || this.faceTrackingDisabledByUser) return;

        this.faceTrackingDisabledByUser = true;
        this.faceTrackerGeneration++;
        this.faceAssetAbortController?.abort();
        this.faceAssetAbortController = null;
        this.cancelFaceFrameLoop();
        this.disposeFaceInferenceBackend();
        this.clearFaceInitializationTicker();
        this.faceInitializationState = 'cancelled';
        this.faceTrackerState = 'disabled';
        this.faceTargetLandmarks = null;
        this.faceSmoothedLandmarks = null;
        this.faceOverlayOpacity = 0;
        this.faceOverlayTargetOpacity = 0;
        this.clearFaceOverlay();
        await this.service.render();
    }

    public async enableFaceTracking() {
        if (!this.faceSpikeEnabled || !this.faceTrackingDisabledByUser) return;

        this.faceTrackingDisabledByUser = false;
        this.faceInitializationState = 'idle';
        this.faceFailureReason = '';
        this.faceFallbackReason = 'none';
        await this.service.render();
        if (this.cameraIsActive) await this.startFaceTracking();
    }

    public async retryFaceTracking() {
        if (this.faceInitializationState !== 'failed') return;

        this.faceTrackerGeneration++;
        this.disposeFaceInferenceBackend();
        this.faceTrackingDisabledByUser = false;
        this.faceInitializationState = 'idle';
        this.faceFailureReason = '';
        this.faceFallbackReason = 'none';
        await this.service.render();
        if (this.cameraIsActive) await this.startFaceTracking();
    }

    private beginFaceInitialization() {
        this.clearFaceInitializationTicker(false);
        this.faceInitializationStartedAt = performance.now();
        this.faceInitializationElapsedMs = 0;
        this.faceAssetLoadTimeMs = 0;
        this.faceLoadTimeMs = 0;
        this.faceFallbackReason = 'none';
        this.faceFailureReason = '';
        this.faceInitializationState = 'asset_loading';
        this.faceInitializationTicker = setInterval(() => {
            if (!this.faceInitializationPending || !this.faceInitializationStartedAt) return;
            this.faceInitializationElapsedMs = this.roundMetric(
                performance.now() - this.faceInitializationStartedAt
            );
            if (!this.destroyed) void this.service.render();
        }, FACE_LANDMARK_SPIKE.uiUpdateIntervalMs);
    }

    private finishFaceInitialization(state: 'model_ready' | 'failed') {
        if (this.faceInitializationStartedAt) {
            this.faceInitializationElapsedMs = this.roundMetric(
                performance.now() - this.faceInitializationStartedAt
            );
        }
        this.faceInitializationState = state;
        this.clearFaceInitializationTicker(false);
    }

    private clearFaceInitializationTicker(updateElapsed: boolean = true) {
        if (updateElapsed && this.faceInitializationStartedAt) {
            this.faceInitializationElapsedMs = this.roundMetric(
                performance.now() - this.faceInitializationStartedAt
            );
        }
        if (this.faceInitializationTicker !== null) {
            clearInterval(this.faceInitializationTicker);
            this.faceInitializationTicker = null;
        }
        this.faceInitializationStartedAt = 0;
    }

    private async preloadFaceAssets(generation: number) {
        this.faceInitializationState = 'asset_loading';
        this.faceAssetAbortController?.abort();
        const controller = new AbortController();
        this.faceAssetAbortController = controller;
        const timeout = setTimeout(() => controller.abort(), FACE_LANDMARK_SPIKE.assetLoadTimeoutMs);
        const startedAt = performance.now();

        try {
            await Promise.all([
                this.preloadFaceAsset(FACE_LANDMARK_SPIKE.loaderScriptPath, controller.signal),
                this.preloadFaceAsset(FACE_LANDMARK_SPIKE.modelAssetPath, controller.signal)
            ]);
            if (
                this.destroyed
                || generation !== this.faceTrackerGeneration
                || !this.cameraIsActive
            ) throw new Error('Face asset loading was cancelled');
            this.faceAssetLoadTimeMs = this.roundMetric(performance.now() - startedAt);
        } finally {
            clearTimeout(timeout);
            if (this.faceAssetAbortController === controller) this.faceAssetAbortController = null;
        }
    }

    private async preloadFaceAsset(path: string, signal: AbortSignal) {
        const url = new URL(path, window.location.href);
        if (url.origin !== window.location.origin) {
            throw new Error(`Cross-origin Face Landmarker asset is not allowed: ${url.origin}`);
        }

        const response = await fetch(url.href, {
            signal,
            cache: 'force-cache',
            credentials: 'same-origin'
        });
        if (!response.ok) {
            throw new Error(`Face Landmarker asset failed: ${response.status} ${url.pathname}`);
        }
        const contentType = (response.headers.get('content-type') || '').toLowerCase();
        if (contentType.includes('text/html')) {
            throw new Error(`Face Landmarker asset resolved to HTML: ${url.pathname}`);
        }
        await response.arrayBuffer();
    }

    private isFaceInferenceBackendReady(): boolean {
        if (this.faceBackend === 'worker') {
            return !!this.faceWorker && this.faceWorkerReady;
        }
        return !!this.faceLandmarker;
    }

    private supportsFaceWorkerInference(): boolean {
        return typeof Worker !== 'undefined'
            && typeof OffscreenCanvas !== 'undefined'
            && typeof createImageBitmap === 'function';
    }

    private async initializeFaceInferenceBackend(generation: number) {
        if (this.supportsFaceWorkerInference()) {
            try {
                this.faceInitializationState = 'worker_initializing';
                await this.service.render();
                await this.initializeFaceWorker();
                return;
            } catch (workerError) {
                const initializationCancelled = this.destroyed
                    || !this.cameraIsActive
                    || generation !== this.faceTrackerGeneration;
                this.disposeFaceWorker();
                if (initializationCancelled) throw workerError;
                this.faceFallbackReason = 'worker_initialization_failed';
                this.faceInitializationState = 'fallback';
                console.info('[MEEO Face Spike]', {
                    state: 'loading',
                    reason: 'worker_initialization_failed_main_thread_fallback',
                    detail: workerError instanceof Error ? workerError.message : String(workerError)
                });
                await this.service.render();
            }
        } else {
            this.faceFallbackReason = 'worker_unsupported';
            this.faceInitializationState = 'fallback';
            console.info('[MEEO Face Spike]', {
                state: 'loading',
                reason: 'worker_unsupported_main_thread_fallback'
            });
            await this.service.render();
        }

        if (
            this.destroyed
            || !this.cameraIsActive
            || generation !== this.faceTrackerGeneration
        ) throw new Error('Face tracker initialization was cancelled');
        await this.initializeMainThreadFaceLandmarker(generation);
    }

    private initializeFaceWorker(): Promise<void> {
        this.disposeFaceWorker();
        this.faceBackend = 'worker';
        this.faceWorkerReady = false;

        return new Promise<void>((resolve, reject) => {
            let worker: Worker;
            try {
                worker = new Worker(FACE_LANDMARK_SPIKE.workerScriptPath, {
                    name: 'meeo-face-landmarker'
                });
            } catch (error) {
                reject(error instanceof Error ? error : new Error(String(error)));
                return;
            }

            this.faceWorker = worker;
            this.faceWorkerInitializationResolve = resolve;
            this.faceWorkerInitializationReject = reject;
            this.faceWorkerInitializationTimeout = setTimeout(() => {
                this.rejectFaceWorkerInitialization(new Error('Face worker initialization timed out'));
            }, FACE_LANDMARK_SPIKE.workerInitializationTimeoutMs);

            worker.onmessage = (event: MessageEvent) => this.handleFaceWorkerMessage(event);
            worker.onerror = (event: ErrorEvent) => {
                event.preventDefault();
                const error = new Error(event.message || 'Face worker failed');
                if (!this.faceWorkerReady) {
                    this.rejectFaceWorkerInitialization(error);
                    return;
                }
                void this.fallbackFromFaceWorker('worker_runtime_error');
            };
            worker.postMessage({
                type: 'initialize',
                config: {
                    loaderScriptPath: FACE_LANDMARK_SPIKE.loaderScriptPath,
                    wasmRoot: FACE_LANDMARK_SPIKE.wasmRoot,
                    modelAssetPath: FACE_LANDMARK_SPIKE.modelAssetPath
                }
            });
        });
    }

    private async initializeMainThreadFaceLandmarker(generation?: number) {
        this.faceBackend = 'main-thread';
        const wasmStartedAt = performance.now();
        const vision = await FilesetResolver.forVisionTasks(FACE_LANDMARK_SPIKE.wasmRoot);
        this.faceWasmLoadTimeMs = Math.round((performance.now() - wasmStartedAt) * 10) / 10;
        const modelStartedAt = performance.now();
        let landmarker: FaceLandmarker;

        try {
            landmarker = await this.createFaceLandmarker(vision, 'GPU');
            this.faceDelegate = 'GPU';
        } catch (gpuError) {
            if (this.faceFallbackReason === 'none') {
                this.faceFallbackReason = 'gpu_delegate_unavailable_cpu_fallback';
            }
            console.info('[MEEO Face Spike]', {
                state: 'loading',
                reason: 'gpu_delegate_unavailable_cpu_fallback'
            });
            landmarker = await this.createFaceLandmarker(vision);
            this.faceDelegate = 'CPU';
        }

        this.faceModelLoadTimeMs = Math.round((performance.now() - modelStartedAt) * 10) / 10;
        if (
            this.destroyed
            || (generation !== undefined && generation !== this.faceTrackerGeneration)
            || !this.cameraIsActive
        ) {
            try { landmarker.close(); } catch (error) { }
            throw new Error('Main-thread Face Landmarker initialization was cancelled');
        }
        this.faceLandmarker = landmarker;
    }

    private createFaceLandmarker(vision: any, delegate?: 'GPU'): Promise<FaceLandmarker> {
        const baseOptions: any = {
            modelAssetPath: FACE_LANDMARK_SPIKE.modelAssetPath
        };
        if (delegate) baseOptions.delegate = delegate;

        return FaceLandmarker.createFromOptions(vision, {
            baseOptions,
            runningMode: 'VIDEO',
            numFaces: 2,
            minFaceDetectionConfidence: 0.5,
            minFacePresenceConfidence: 0.5,
            minTrackingConfidence: 0.5,
            outputFaceBlendshapes: false,
            outputFacialTransformationMatrixes: false
        });
    }

    private scheduleFaceFrame() {
        const video = this.cameraVideo?.nativeElement;
        if (this.destroyed || !this.isFaceInferenceBackendReady() || !this.cameraIsActive || !video) return;

        const requestVideoFrame = (video as any).requestVideoFrameCallback;
        if (typeof requestVideoFrame === 'function') {
            if (this.faceVideoFrameCallback !== null) return;
            this.faceVideoFrameCallback = requestVideoFrame.call(video, (timestamp: number, metadata: any) => {
                this.faceVideoFrameCallback = null;
                this.scheduleFaceFrame();
                this.processFaceFrame(timestamp, metadata);
            });
            return;
        }

        if (this.faceFallbackAnimationFrame !== null) return;
        this.faceFallbackAnimationFrame = requestAnimationFrame((timestamp) => {
            this.faceFallbackAnimationFrame = null;
            this.scheduleFaceFrame();
            this.processFaceFrame(timestamp, {
                mediaTime: video.currentTime,
                presentedFrames: undefined
            });
        });
    }

    private processFaceFrame(timestamp: number, metadata: any = {}) {
        const video = this.cameraVideo?.nativeElement;

        if (this.destroyed || !this.isFaceInferenceBackendReady() || !this.cameraIsActive || !video) return;

        this.recordCameraFramePerformance(timestamp, metadata);
        this.faceEffectiveTargetFps = this.calculateEffectiveInferenceTargetFps();
        const frameInterval = 1000 / this.faceEffectiveTargetFps;
        const frameIsReady = video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0;
        const mediaTime = Number.isFinite(metadata?.mediaTime) ? metadata.mediaTime : video.currentTime;
        const hasNewVideoFrame = mediaTime !== this.faceLastVideoTime;
        const intervalElapsed = timestamp - this.faceLastDetectionAt >= frameInterval;

        if (
            frameIsReady
            && hasNewVideoFrame
            && intervalElapsed
            && !(this.faceBackend === 'worker' && this.faceWorkerBusy)
        ) {
            this.faceLastVideoTime = mediaTime;
            this.faceLastDetectionAt = timestamp;
            const latencyStartedAt = this.absolutePerformanceNow();

            if (this.faceBackend === 'worker') {
                void this.sendFaceFrameToWorker(video, timestamp, latencyStartedAt);
                return;
            }

            const inputStartedAt = performance.now();
            const inferenceCanvas = this.prepareFaceInferenceInput(video);
            const inputDuration = performance.now() - inputStartedAt;
            if (!inferenceCanvas) return;
            const inferenceStartedAt = performance.now();

            try {
                const landmarker = this.faceLandmarker;
                if (!landmarker) return;
                const result = landmarker.detectForVideo(inferenceCanvas, timestamp);
                const inferenceDuration = performance.now() - inferenceStartedAt;
                const latency = this.absolutePerformanceNow() - latencyStartedAt;
                this.recordFaceTrackerPerformance(timestamp, inputDuration, inferenceDuration, 0, latency);
                this.handleFaceLandmarkerResult(result, timestamp);
            } catch (error) {
                this.setFaceTrackerState('error', 'video_inference_failed');
                this.faceOverlayTargetOpacity = 0;
                this.cancelFaceFrameLoop();
                this.clearFaceOverlay();
                return;
            }
        }
    }

    private async sendFaceFrameToWorker(
        video: HTMLVideoElement,
        timestamp: number,
        captureStartedAt: number
    ) {
        const worker = this.faceWorker;
        const generation = this.faceTrackerGeneration;
        if (!worker || !this.faceWorkerReady || this.faceWorkerBusy) return;

        this.faceWorkerBusy = true;
        const dimensions = this.calculateFaceInferenceDimensions(video);
        this.faceInferenceWidth = dimensions.width;
        this.faceInferenceHeight = dimensions.height;

        try {
            const frame = await this.createFaceFrameBitmap(video, dimensions.width, dimensions.height);
            if (
                this.destroyed
                || generation !== this.faceTrackerGeneration
                || this.faceBackend !== 'worker'
                || this.faceWorker !== worker
                || !this.cameraIsActive
            ) {
                frame.close();
                this.faceWorkerBusy = false;
                return;
            }

            const sentAt = this.absolutePerformanceNow();
            worker.postMessage({
                type: 'frame',
                requestId: ++this.faceWorkerRequestId,
                generation,
                timestamp,
                captureStartedAt,
                sentAt,
                mainInputTimeMs: sentAt - captureStartedAt,
                width: dimensions.width,
                height: dimensions.height,
                frame
            }, [frame]);
        } catch (error) {
            this.faceWorkerBusy = false;
            await this.fallbackFromFaceWorker('worker_frame_transfer_failed');
        }
    }

    private async createFaceFrameBitmap(
        video: HTMLVideoElement,
        width: number,
        height: number
    ): Promise<ImageBitmap> {
        try {
            return await createImageBitmap(video, {
                resizeWidth: width,
                resizeHeight: height,
                resizeQuality: 'low'
            });
        } catch (directTransferError) {
            const canvas = this.prepareFaceInferenceInput(video);
            if (!canvas) throw directTransferError;
            return createImageBitmap(canvas);
        }
    }

    private calculateFaceInferenceDimensions(video: HTMLVideoElement): { width: number; height: number } {
        const longEdge = this.faceIsCoarseDevice
            ? FACE_LANDMARK_SPIKE.mobileInferenceLongEdge
            : FACE_LANDMARK_SPIKE.desktopInferenceLongEdge;
        const scale = Math.min(1, longEdge / Math.max(video.videoWidth, video.videoHeight));
        return {
            width: Math.max(2, Math.round(video.videoWidth * scale)),
            height: Math.max(2, Math.round(video.videoHeight * scale))
        };
    }

    private prepareFaceInferenceInput(video: HTMLVideoElement): HTMLCanvasElement | null {
        if (!this.faceInferenceCanvas) {
            this.faceInferenceCanvas = document.createElement('canvas');
            this.faceInferenceContext = this.faceInferenceCanvas.getContext('2d', {
                alpha: false,
                desynchronized: true
            } as any);
        }

        const canvas = this.faceInferenceCanvas;
        const context = this.faceInferenceContext;
        if (!canvas || !context || !video.videoWidth || !video.videoHeight) return null;

        const { width, height } = this.calculateFaceInferenceDimensions(video);

        if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width;
            canvas.height = height;
        }
        this.faceInferenceWidth = width;
        this.faceInferenceHeight = height;

        context.drawImage(video, 0, 0, width, height);
        return canvas;
    }

    private calculateEffectiveInferenceTargetFps(): number {
        if (!this.faceAverageLatencyMs) return this.faceTargetFps;
        if (this.faceBackend === 'worker') return this.faceTargetFps;

        const dutyCycle = this.faceIsCoarseDevice
            ? FACE_LANDMARK_SPIKE.mobileInferenceDutyCycle
            : FACE_LANDMARK_SPIKE.desktopInferenceDutyCycle;
        const budgetedFps = (1000 * dutyCycle) / this.faceAverageLatencyMs;
        return Math.round(Math.max(
            FACE_LANDMARK_SPIKE.minimumTargetFps,
            Math.min(this.faceTargetFps, budgetedFps)
        ) * 10) / 10;
    }

    private recordCameraFramePerformance(timestamp: number, metadata: any) {
        const presentedFrames = Number(metadata?.presentedFrames);
        const mediaTime = Number(metadata?.mediaTime);

        if (
            Number.isFinite(presentedFrames)
            && Number.isFinite(mediaTime)
            && this.faceLastPresentedFrames !== null
            && this.faceLastMediaTime !== null
        ) {
            const frameDelta = presentedFrames - this.faceLastPresentedFrames;
            const timeDelta = mediaTime - this.faceLastMediaTime;
            const fps = timeDelta > 0 ? frameDelta / timeDelta : 0;
            if (fps > 0 && fps <= 120) {
                this.faceCameraFpsSamples.push(fps);
                this.faceCameraFpsSamples = this.faceCameraFpsSamples.slice(-30);
                this.faceCameraFps = this.averageMetric(this.faceCameraFpsSamples);
            }
        } else {
            this.faceCameraFrameTimes.push(timestamp);
            this.faceCameraFrameTimes = this.recentMetricTimes(this.faceCameraFrameTimes, timestamp);
            this.faceCameraFps = this.calculateWindowFps(this.faceCameraFrameTimes);
        }

        if (Number.isFinite(presentedFrames)) this.faceLastPresentedFrames = presentedFrames;
        if (Number.isFinite(mediaTime)) this.faceLastMediaTime = mediaTime;
    }

    private handleFaceWorkerMessage(event: MessageEvent) {
        const message = event.data || {};

        if (message.type === 'initialized') {
            this.faceWorkerReady = true;
            this.faceDelegate = ['GPU', 'CPU'].includes(message.delegate)
                ? message.delegate
                : 'unknown';
            this.faceWasmLoadTimeMs = this.roundMetric(message.wasmLoadTimeMs);
            this.faceModelLoadTimeMs = this.roundMetric(message.modelLoadTimeMs);
            this.resolveFaceWorkerInitialization();
            return;
        }

        if (message.type === 'error') {
            this.faceWorkerBusy = false;
            const error = new Error(message.message || 'Face worker failed');
            if (message.stage === 'initialization' && !this.faceWorkerReady) {
                this.rejectFaceWorkerInitialization(error);
                return;
            }
            void this.fallbackFromFaceWorker('worker_inference_failed');
            return;
        }

        if (message.type !== 'result' || this.faceBackend !== 'worker') return;
        this.faceWorkerBusy = false;
        if (message.generation !== this.faceTrackerGeneration) return;
        const receivedAt = this.absolutePerformanceNow();
        const inboundLatency = Math.max(0, Number(message.workerReceivedAt) - Number(message.sentAt));
        const outboundLatency = Math.max(0, receivedAt - Number(message.responseSentAt));
        const postMessageLatency = inboundLatency + outboundLatency;
        const inputDuration = Math.max(0, Number(message.mainInputTimeMs))
            + Math.max(0, Number(message.workerInputTimeMs));
        const totalLatency = Math.max(0, receivedAt - Number(message.captureStartedAt));
        const landmarks = Array.isArray(message.faceLandmarks)
            ? message.faceLandmarks.map((face: any[]) => this.hydrateFaceWorkerLandmarks(face))
            : [];

        this.recordFaceTrackerPerformance(
            performance.now(),
            inputDuration,
            Math.max(0, Number(message.inferenceTimeMs)),
            postMessageLatency,
            totalLatency
        );
        this.handleFaceLandmarkerResult({ faceLandmarks: landmarks }, Number(message.timestamp));
    }

    private hydrateFaceWorkerLandmarks(serialized: any[]): any[] {
        const landmarks: any[] = [];
        if (!Array.isArray(serialized)) return landmarks;
        serialized.forEach((landmark) => {
            const index = Number(landmark?.index);
            if (!Number.isInteger(index)) return;
            landmarks[index] = {
                x: Number(landmark.x),
                y: Number(landmark.y),
                z: Number(landmark.z) || 0
            };
        });
        return landmarks;
    }

    private recordFaceTrackerPerformance(
        timestamp: number,
        inputDuration: number,
        inferenceDuration: number,
        postMessageLatency: number,
        latency: number
    ) {
        this.faceDetectionTimes.push(timestamp);
        this.faceDetectionTimes = this.recentMetricTimes(this.faceDetectionTimes, timestamp);
        this.faceInputPrepDurations.push(inputDuration);
        this.faceInputPrepDurations = this.faceInputPrepDurations.slice(-30);
        this.faceInferenceDurations.push(inferenceDuration);
        this.faceInferenceDurations = this.faceInferenceDurations.slice(-30);
        this.facePostMessageDurations.push(postMessageLatency);
        this.facePostMessageDurations = this.facePostMessageDurations.slice(-30);
        this.faceLatencyDurations.push(latency);
        this.faceLatencyDurations = this.faceLatencyDurations.slice(-30);

        this.faceTrackerFps = this.calculateWindowFps(this.faceDetectionTimes);
        this.faceInputPrepMs = this.averageMetric(this.faceInputPrepDurations);
        this.faceInferenceMs = this.averageMetric(this.faceInferenceDurations);
        this.facePostMessageMs = this.averageMetric(this.facePostMessageDurations);
        this.faceAverageLatencyMs = this.averageMetric(this.faceLatencyDurations);

        if (timestamp - this.faceLastUiUpdateAt >= FACE_LANDMARK_SPIKE.uiUpdateIntervalMs) {
            this.faceLastUiUpdateAt = timestamp;
            if (!this.destroyed) void this.service.render();
        }
    }

    private handleFaceLandmarkerResult(result: any, timestamp: number) {
        const faces = Array.isArray(result?.faceLandmarks) ? result.faceLandmarks : [];
        this.faceCount = faces.length;

        if (faces.length === 0) {
            this.setFaceTrackerState('no_face', 'no_face_detected');
            this.faceTargetLandmarks = null;
            this.faceOverlayTargetOpacity = 0;
            return;
        }

        if (faces.length > 1) {
            this.setFaceTrackerState('multiple_faces', 'multiple_faces_detected');
            this.faceTargetLandmarks = null;
            this.faceOverlayTargetOpacity = 0;
            return;
        }

        const landmarks = faces[0];
        if (timestamp - this.faceLastPoseCheckAt >= FACE_LANDMARK_SPIKE.poseCheckIntervalMs) {
            this.faceLastPoseCheckAt = timestamp;
            this.faceRotationOutsideRange = this.isHeadRotationOutsideSpikeRange(landmarks);
        }
        const rotated = this.faceRotationOutsideRange;
        const degraded = this.isFaceTrackerDegraded(timestamp);

        if (rotated) {
            this.setFaceTrackerState('rotated', 'head_rotation_outside_range');
        } else if (degraded) {
            this.setFaceTrackerState('degraded', 'frame_budget_exceeded');
        } else {
            this.setFaceTrackerState('tracking', 'single_face_tracking');
        }

        this.faceTargetLandmarks = this.pickFaceOverlayLandmarks(landmarks);
        if (!this.faceSmoothedLandmarks) {
            this.faceSmoothedLandmarks = this.cloneFaceLandmarks(this.faceTargetLandmarks);
        }
        this.faceOverlayAllowsEffects = !rotated;
        this.faceOverlayTargetOpacity = 1;
    }

    private scheduleFaceOverlayFrame() {
        if (
            this.faceOverlayAnimationFrame !== null
            || this.destroyed
            || !this.isFaceInferenceBackendReady()
            || !this.cameraIsActive
        ) return;

        this.faceOverlayAnimationFrame = requestAnimationFrame((timestamp) => {
            this.faceOverlayAnimationFrame = null;
            this.renderSmoothedFaceOverlay(timestamp);
            this.scheduleFaceOverlayFrame();
        });
    }

    private renderSmoothedFaceOverlay(timestamp: number) {
        const previousTimestamp = this.faceLastOverlayAt || timestamp;
        const elapsed = Math.min(100, Math.max(0, timestamp - previousTimestamp));
        this.faceLastOverlayAt = timestamp;

        if (this.faceTargetLandmarks) {
            if (!this.faceSmoothedLandmarks) {
                this.faceSmoothedLandmarks = this.cloneFaceLandmarks(this.faceTargetLandmarks);
            } else {
                const blend = 1 - Math.exp(-elapsed / FACE_LANDMARK_SPIKE.overlaySmoothingMs);
                FACE_OVERLAY_LANDMARK_INDICES.forEach((index) => {
                    const target = this.faceTargetLandmarks?.[index];
                    const current = this.faceSmoothedLandmarks?.[index];
                    if (!target) return;
                    if (!current) {
                        if (this.faceSmoothedLandmarks) this.faceSmoothedLandmarks[index] = { ...target };
                        return;
                    }
                    current.x += (target.x - current.x) * blend;
                    current.y += (target.y - current.y) * blend;
                    if (Number.isFinite(target.z)) current.z += (target.z - current.z) * blend;
                });
            }
        }

        const opacityDuration = this.faceOverlayTargetOpacity > this.faceOverlayOpacity
            ? FACE_LANDMARK_SPIKE.overlaySmoothingMs
            : FACE_LANDMARK_SPIKE.overlayFadeMs;
        const opacityBlend = 1 - Math.exp(-elapsed / opacityDuration);
        this.faceOverlayOpacity += (this.faceOverlayTargetOpacity - this.faceOverlayOpacity) * opacityBlend;

        if (!this.faceSmoothedLandmarks || this.faceOverlayOpacity < 0.01) {
            if (this.faceOverlayTargetOpacity === 0) this.faceSmoothedLandmarks = null;
            this.clearFaceOverlay();
            return;
        }

        const overlayStartedAt = performance.now();
        this.drawFaceOverlay(
            this.faceSmoothedLandmarks,
            this.faceOverlayAllowsEffects,
            this.faceOverlayOpacity
        );
        const overlayDuration = performance.now() - overlayStartedAt;
        this.faceOverlayTimes.push(timestamp);
        this.faceOverlayTimes = this.recentMetricTimes(this.faceOverlayTimes, timestamp);
        this.faceOverlayDurations.push(overlayDuration);
        this.faceOverlayDurations = this.faceOverlayDurations.slice(-60);
        this.faceOverlayFps = this.calculateWindowFps(this.faceOverlayTimes);
        this.faceOverlayMs = this.averageMetric(this.faceOverlayDurations);
    }

    private pickFaceOverlayLandmarks(landmarks: any[]): any[] {
        const selected: any[] = [];
        FACE_OVERLAY_LANDMARK_INDICES.forEach((index) => {
            const landmark = landmarks?.[index];
            if (landmark) selected[index] = { x: landmark.x, y: landmark.y, z: landmark.z || 0 };
        });
        return selected;
    }

    private cloneFaceLandmarks(landmarks: any[]): any[] {
        const cloned: any[] = [];
        FACE_OVERLAY_LANDMARK_INDICES.forEach((index) => {
            const landmark = landmarks?.[index];
            if (landmark) cloned[index] = { ...landmark };
        });
        return cloned;
    }

    private recentMetricTimes(times: number[], timestamp: number): number[] {
        return times.filter((time) => timestamp - time <= FACE_LANDMARK_SPIKE.metricWindowMs);
    }

    private calculateWindowFps(times: number[]): number {
        if (times.length < 2) return 0;
        const elapsed = times[times.length - 1] - times[0];
        const fps = elapsed > 0 ? ((times.length - 1) * 1000) / elapsed : 0;
        return Math.round(fps * 10) / 10;
    }

    private averageMetric(values: number[]): number {
        if (!values.length) return 0;
        const total = values.reduce((sum, value) => sum + value, 0);
        return Math.round((total / values.length) * 10) / 10;
    }

    private roundMetric(value: number): number {
        const metric = Number(value);
        return Number.isFinite(metric) ? Math.round(metric * 10) / 10 : 0;
    }

    private absolutePerformanceNow(): number {
        return performance.timeOrigin + performance.now();
    }

    private isHeadRotationOutsideSpikeRange(landmarks: any[]): boolean {
        const rightEyeOuter = landmarks?.[33];
        const leftEyeOuter = landmarks?.[263];
        const noseTip = landmarks?.[1];
        if (!rightEyeOuter || !leftEyeOuter || !noseTip) return false;

        const eyeSpan = Math.abs(leftEyeOuter.x - rightEyeOuter.x);
        if (eyeSpan < 0.02) return true;

        const eyeCenterX = (rightEyeOuter.x + leftEyeOuter.x) / 2;
        const yawOffset = Math.abs(noseTip.x - eyeCenterX) / (eyeSpan / 2);
        const rollDegrees = Math.abs(Math.atan2(
            leftEyeOuter.y - rightEyeOuter.y,
            leftEyeOuter.x - rightEyeOuter.x
        ) * 180 / Math.PI);

        return yawOffset > 0.42 || rollDegrees > 18;
    }

    private isFaceTrackerDegraded(timestamp: number): boolean {
        const hasStableSample = this.faceDetectionTimes.length >= 6
            && timestamp - this.faceTrackingStartedAt >= 3000;
        const lowFps = hasStableSample && this.faceTrackerFps < this.faceEffectiveTargetFps * 0.75;
        return this.faceAverageLatencyMs > 100 || lowFps;
    }

    private applyFaceMapOffsets(landmarks: any[]): any[] {
        if (!this.faceMapReady || !this.faceMap) return landmarks;
        const corrected = this.cloneFaceLandmarks(landmarks);
        FACE_MAP_POINT_DEFINITIONS.forEach((definition) => {
            const current = corrected[definition.index];
            const mapped = this.faceMap?.points[definition.id];
            if (!current || !mapped) return;
            current.x = this.clampNormalized(current.x + mapped.offsetX);
            current.y = this.clampNormalized(current.y + mapped.offsetY);
        });
        return corrected;
    }

    private drawFaceOverlay(landmarks: any[], allowFilterEffects: boolean, opacity: number = 1) {
        const canvas = this.landmarkCanvas?.nativeElement;
        const video = this.cameraVideo?.nativeElement;
        if (!canvas || !video || !landmarks?.length) return;

        const width = canvas.clientWidth;
        const height = canvas.clientHeight;
        if (!width || !height || !video.videoWidth || !video.videoHeight) return;

        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const pixelWidth = Math.max(1, Math.round(width * dpr));
        const pixelHeight = Math.max(1, Math.round(height * dpr));
        if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
            canvas.width = pixelWidth;
            canvas.height = pixelHeight;
        }

        const context = canvas.getContext('2d');
        if (!context) return;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
        context.clearRect(0, 0, width, height);
        context.save();
        context.globalAlpha = Math.max(0, Math.min(1, opacity));

        const mappedLandmarks = this.applyFaceMapOffsets(landmarks);
        const mapPoint = (index: number) => this.mapLandmarkToPreview(
            mappedLandmarks[index],
            width,
            height,
            video.videoWidth,
            video.videoHeight
        );

        if (allowFilterEffects && !this.showOriginal) {
            this.drawFaceMapRegionEffects(context, video, width, height, mapPoint, this.faceMapReady);
        }

        if (this.showOriginal || !this.faceRecognitionPointsVisible) {
            context.restore();
            return;
        }

        const eyeContours = [
            [33, 160, 158, 133, 153, 144],
            [362, 385, 387, 263, 373, 380]
        ];
        const eyebrowContours = [
            [70, 63, 105, 66, 107],
            [336, 296, 334, 293, 300]
        ];
        const noseContour = [168, 1, 98, 327];
        const lipContour = [61, 13, 291, 14];
        const jawContour = [234, 93, 132, 58, 172, 136, 150, 149, 176, 148, 152, 377, 400, 378, 379, 365, 397, 288, 361, 323, 454];

        eyeContours.forEach((indices) => {
            this.drawLandmarkPath(context, indices, mapPoint, 'rgba(220, 239, 229, 0.72)', 1.15, true);
        });
        if (this.faceMapReady) {
            eyebrowContours.forEach((indices) => {
                this.drawLandmarkPath(context, indices, mapPoint, 'rgba(216, 201, 242, 0.48)', 1.05, false);
            });
            this.drawLandmarkPath(context, noseContour, mapPoint, 'rgba(239, 194, 158, 0.5)', 1.05, false);
            this.drawLandmarkPath(context, lipContour, mapPoint, 'rgba(239, 180, 187, 0.58)', 1.05, true);
        }
        this.drawLandmarkPath(context, jawContour, mapPoint, 'rgba(220, 239, 229, 0.52)', 1.1, false);

        const keyPoints = this.faceMapReady
            ? FACE_MAP_POINT_DEFINITIONS.map((definition) => ({
                index: definition.index,
                color: this.faceMapPointColor(definition.region)
            }))
            : [
                { index: 33, color: '#d8c9f2' },
                { index: 263, color: '#d8c9f2' },
                { index: 1, color: '#efc29e' },
                { index: 61, color: '#efb4bb' },
                { index: 291, color: '#efb4bb' },
                { index: 152, color: '#bde0cd' }
            ];
        keyPoints.forEach((item) => {
            const point = mapPoint(item.index);
            if (!point) return;
            context.beginPath();
            context.arc(point.x, point.y, 3.1, 0, Math.PI * 2);
            context.fillStyle = item.color;
            context.fill();
            context.lineWidth = 1;
            context.strokeStyle = 'rgba(20, 32, 26, 0.72)';
            context.stroke();
        });
        context.restore();
    }

    private drawFaceMapRegionEffects(
        context: CanvasRenderingContext2D,
        video: HTMLVideoElement,
        previewWidth: number,
        previewHeight: number,
        mapPoint: (index: number) => { x: number; y: number } | null,
        allowFaceGeometry: boolean
    ) {
        const eyeStrength = allowFaceGeometry ? this.faceEffectStrength('face-eyes-scale') : 0;
        const eyeDefinitionStrength = this.faceEffectStrength('eye-definition');
        const lipStrength = allowFaceGeometry ? this.faceEffectStrength('face-lips-volume') : 0;
        const noseStrength = allowFaceGeometry ? this.faceEffectStrength('face-nose-highlight') : 0;
        const jawWidthStrength = allowFaceGeometry ? this.faceEffectStrength('face-jaw-width') : 0;
        const jawVlineStrength = allowFaceGeometry ? this.faceEffectStrength('face-jaw-vline') : 0;
        const chinTaperStrength = allowFaceGeometry ? this.faceEffectStrength('face-chin-taper') : 0;
        const sculptStrength = this.faceEffectStrength('sculpt-light');
        const jawDirection = this.filterDirection('face-jaw-width');
        const browStrength = allowFaceGeometry ? this.faceEffectStrength('face-brows-lift') : 0;

        if (eyeStrength > 0 || eyeDefinitionStrength > 0) {
            [
                [33, 133, 160, 144],
                [362, 263, 385, 380]
            ].forEach(([outerIndex, innerIndex, topIndex, bottomIndex]) => {
                const outer = mapPoint(outerIndex);
                const inner = mapPoint(innerIndex);
                const top = mapPoint(topIndex);
                const bottom = mapPoint(bottomIndex);
                if (!outer || !inner || !top || !bottom) return;
                const center = this.midpoint(outer, inner);
                const eyeWidth = this.pointDistance(outer, inner);
                const eyeHeight = Math.max(this.pointDistance(top, bottom), eyeWidth * 0.28);
                const rotation = Math.atan2(inner.y - outer.y, inner.x - outer.x);
                const localFilter = this.composeCanvasFilter(
                    this.previewFilter,
                    eyeDefinitionStrength > 0
                        ? 'contrast(' + (1 + eyeDefinitionStrength * 0.12).toFixed(3)
                            + ') saturate(' + (1 + eyeDefinitionStrength * 0.045).toFixed(3) + ')'
                        : ''
                );
                this.drawFeatheredVideoPatch(
                    context,
                    video,
                    previewWidth,
                    previewHeight,
                    {
                        center,
                        radiusX: eyeWidth * 0.72,
                        radiusY: eyeHeight * 1.18,
                        scaleX: 1 + eyeStrength * FACE_BEAUTY_RENDERER.eyeMaxScaleX,
                        scaleY: 1 + eyeStrength * FACE_BEAUTY_RENDERER.eyeMaxScaleY,
                        rotation,
                        feather: 0.42,
                        filter: localFilter
                    }
                );
            });
        }

        if (lipStrength > 0) {
            const left = mapPoint(61);
            const right = mapPoint(291);
            const top = mapPoint(13);
            const bottom = mapPoint(14);
            if (left && right && top && bottom) {
                const center = this.midpoint(top, bottom);
                const lipWidth = this.pointDistance(left, right);
                const lipHeight = Math.max(this.pointDistance(top, bottom), lipWidth * 0.12);
                this.drawFeatheredVideoPatch(
                    context,
                    video,
                    previewWidth,
                    previewHeight,
                    {
                        center,
                        radiusX: lipWidth * 0.62,
                        radiusY: lipHeight * 1.72,
                        scaleX: 1 + lipStrength * FACE_BEAUTY_RENDERER.lipMaxScaleX,
                        scaleY: 1 + lipStrength * FACE_BEAUTY_RENDERER.lipMaxScaleY,
                        rotation: Math.atan2(right.y - left.y, right.x - left.x),
                        feather: 0.44
                    }
                );
            }
        }

        if (noseStrength > 0) {
            const bridge = mapPoint(168);
            const tip = mapPoint(1);
            const left = mapPoint(98);
            const right = mapPoint(327);
            if (bridge && tip && left && right) {
                const center = this.midpoint(bridge, tip);
                const noseWidth = this.pointDistance(left, right);
                const noseHeight = this.pointDistance(bridge, tip);
                this.drawFeatheredVideoPatch(
                    context,
                    video,
                    previewWidth,
                    previewHeight,
                    {
                        center,
                        radiusX: noseWidth * 0.82,
                        radiusY: noseHeight * 0.78,
                        scaleX: 1 - noseStrength * 0.018,
                        scaleY: 1 + noseStrength * FACE_BEAUTY_RENDERER.noseMaxScaleY,
                        translateY: -noseHeight * noseStrength * 0.022,
                        feather: 0.46
                    }
                );

                const gradient = context.createLinearGradient(bridge.x, bridge.y, tip.x, tip.y);
                gradient.addColorStop(0, 'rgba(255, 247, 225, 0)');
                gradient.addColorStop(0.48, 'rgba(255, 247, 225, ' + (0.08 + noseStrength * 0.18).toFixed(3) + ')');
                gradient.addColorStop(1, 'rgba(255, 247, 225, 0)');
                context.save();
                context.globalCompositeOperation = 'screen';
                context.strokeStyle = gradient;
                context.lineWidth = Math.max(1.25, noseWidth * (0.065 + noseStrength * 0.055));
                context.lineCap = 'round';
                context.shadowColor = 'rgba(255, 244, 219, ' + (noseStrength * 0.24).toFixed(3) + ')';
                context.shadowBlur = Math.max(2, noseWidth * 0.12);
                context.beginPath();
                context.moveTo(bridge.x, bridge.y);
                context.lineTo(tip.x, tip.y);
                context.stroke();
                context.restore();
            }
        }

        if (jawWidthStrength > 0 || jawVlineStrength > 0 || chinTaperStrength > 0 || sculptStrength > 0) {
            const left = mapPoint(234);
            const right = mapPoint(454);
            const chin = mapPoint(152);
            if (left && right && chin) {
                const jawWidth = this.pointDistance(left, right);
                const cheekCenter = this.midpoint(left, right);
                const jawHeight = this.pointDistance(cheekCenter, chin);
                if (jawWidthStrength > 0 || jawVlineStrength > 0 || chinTaperStrength > 0) {
                    this.drawJawLowerFaceWarp(
                        context,
                        video,
                        previewWidth,
                        previewHeight,
                        cheekCenter,
                        chin,
                        mapPoint(132) || this.lerpPoint(left, chin, 0.26),
                        mapPoint(172) || this.lerpPoint(left, chin, 0.50),
                        mapPoint(150) || this.lerpPoint(left, chin, 0.74),
                        mapPoint(361) || this.lerpPoint(right, chin, 0.26),
                        mapPoint(397) || this.lerpPoint(right, chin, 0.50),
                        mapPoint(379) || this.lerpPoint(right, chin, 0.74),
                        jawWidth,
                        jawHeight,
                        jawWidthStrength,
                        jawVlineStrength,
                        chinTaperStrength,
                        jawDirection
                    );
                }
                if (sculptStrength > 0) {
                    this.drawSculptLightOverlay(
                        context,
                        mapPoint(172) || this.lerpPoint(left, chin, 0.46),
                        mapPoint(397) || this.lerpPoint(right, chin, 0.46),
                        chin,
                        jawWidth,
                        sculptStrength
                    );
                }
            }
        }

        if (browStrength > 0) {
            [
                [70, 107],
                [336, 300]
            ].forEach(([outerIndex, innerIndex]) => {
                const outer = mapPoint(outerIndex);
                const inner = mapPoint(innerIndex);
                if (!outer || !inner) return;
                const center = this.midpoint(outer, inner);
                const browWidth = this.pointDistance(outer, inner);
                this.drawFeatheredVideoPatch(
                    context,
                    video,
                    previewWidth,
                    previewHeight,
                    {
                        center,
                        radiusX: browWidth * 0.7,
                        radiusY: browWidth * 0.25,
                        translateY: -browWidth * browStrength * 0.065,
                        rotation: Math.atan2(inner.y - outer.y, inner.x - outer.x),
                        feather: 0.46
                    }
                );
            });
        }
    }

    private drawJawLowerFaceWarp(
        context: CanvasRenderingContext2D,
        video: HTMLVideoElement,
        previewWidth: number,
        previewHeight: number,
        faceCenter: { x: number; y: number },
        chin: { x: number; y: number },
        leftUpper: { x: number; y: number },
        leftLower: { x: number; y: number },
        leftNearChin: { x: number; y: number },
        rightUpper: { x: number; y: number },
        rightLower: { x: number; y: number },
        rightNearChin: { x: number; y: number },
        jawWidth: number,
        jawHeight: number,
        jawWidthStrength: number,
        jawVlineStrength: number,
        chinTaperStrength: number,
        jawDirection: number
    ) {
        const isNarrowing = jawDirection < 0;
        const widthDirection = isNarrowing ? 1 : -1;
        const sides = [
            { points: [leftUpper, leftLower, leftNearChin], isLeft: true },
            { points: [rightUpper, rightLower, rightNearChin], isLeft: false }
        ];

        sides.forEach((side) => {
            side.points.forEach((point, index) => {
                const lowerFaceProgress = [0.28, 0.62, 0.88][index];
                const widthShift = jawWidth
                    * jawWidthStrength
                    * FACE_BEAUTY_RENDERER.jawMaxInwardShift
                    * (1 - lowerFaceProgress * 0.34)
                    * widthDirection;
                const vlineShift = jawWidth
                    * jawVlineStrength
                    * FACE_BEAUTY_RENDERER.jawVlineMaxInwardShift
                    * lowerFaceProgress;
                const taperShift = jawWidth
                    * chinTaperStrength
                    * FACE_BEAUTY_RENDERER.jawVlineMaxInwardShift
                    * Math.max(0, (lowerFaceProgress - 0.42) / 0.58);
                const inwardDistance = widthShift + vlineShift + taperShift;
                const translateX = side.isLeft ? inwardDistance : -inwardDistance;
                const localScale = jawWidthStrength * FACE_BEAUTY_RENDERER.jawMaxLocalScale
                    + jawVlineStrength * FACE_BEAUTY_RENDERER.jawMaxLocalScale * lowerFaceProgress
                    + chinTaperStrength * FACE_BEAUTY_RENDERER.chinMaxTaperScale * lowerFaceProgress;
                const widthScaleDirection = jawWidthStrength > 0 && !isNarrowing
                    ? 1
                    : -1;

                this.drawFeatheredVideoPatch(
                    context,
                    video,
                    previewWidth,
                    previewHeight,
                    {
                        center: point,
                        radiusX: jawWidth * (0.16 - lowerFaceProgress * 0.025),
                        radiusY: jawHeight * (0.34 + lowerFaceProgress * 0.10),
                        scaleX: 1 + widthScaleDirection * localScale,
                        scaleY: 1,
                        translateX,
                        feather: 0.52
                    }
                );
            });
        });

        const chinStrength = Math.max(jawVlineStrength * 0.72, chinTaperStrength);
        if (chinStrength <= 0) return;
        const lowerCenter = this.midpoint(leftNearChin, rightNearChin);
        this.drawFeatheredVideoPatch(
            context,
            video,
            previewWidth,
            previewHeight,
            {
                center: this.lerpPoint(lowerCenter, chin, 0.68),
                radiusX: jawWidth * 0.205,
                radiusY: jawHeight * 0.38,
                scaleX: 1 - chinStrength * FACE_BEAUTY_RENDERER.chinMaxTaperScale,
                scaleY: 1 + chinStrength * 0.012,
                translateY: jawHeight * chinStrength * 0.008,
                feather: 0.54
            }
        );
    }

    private drawSculptLightOverlay(
        context: CanvasRenderingContext2D,
        leftLower: { x: number; y: number },
        rightLower: { x: number; y: number },
        chin: { x: number; y: number },
        jawWidth: number,
        strength: number
    ) {
        context.save();
        context.globalCompositeOperation = 'multiply';
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.lineWidth = Math.max(2, jawWidth * 0.018);
        context.shadowColor = 'rgba(44, 35, 29, ' + (strength * 0.08).toFixed(3) + ')';
        context.shadowBlur = Math.max(4, jawWidth * 0.025);
        [leftLower, rightLower].forEach((sidePoint) => {
            const gradient = context.createLinearGradient(sidePoint.x, sidePoint.y, chin.x, chin.y);
            gradient.addColorStop(0, 'rgba(55, 44, 36, 0)');
            gradient.addColorStop(0.48, 'rgba(55, 44, 36, ' + (strength * 0.055).toFixed(3) + ')');
            gradient.addColorStop(1, 'rgba(55, 44, 36, 0)');
            context.strokeStyle = gradient;
            context.beginPath();
            context.moveTo(sidePoint.x, sidePoint.y);
            context.quadraticCurveTo(
                sidePoint.x + (chin.x - sidePoint.x) * 0.48,
                sidePoint.y + (chin.y - sidePoint.y) * 0.76,
                chin.x,
                chin.y
            );
            context.stroke();
        });
        context.restore();
    }

    private drawFeatheredVideoPatch(
        context: CanvasRenderingContext2D,
        video: HTMLVideoElement,
        previewWidth: number,
        previewHeight: number,
        options: FacePatchOptions
    ) {
        const radiusX = Number(options.radiusX);
        const radiusY = Number(options.radiusY);
        if (
            !video.videoWidth
            || !video.videoHeight
            || radiusX <= 0
            || radiusY <= 0
            || typeof document === 'undefined'
        ) return;

        const center = options.center;
        const scaleX = Number.isFinite(options.scaleX) ? Number(options.scaleX) : 1;
        const scaleY = Number.isFinite(options.scaleY) ? Number(options.scaleY) : 1;
        const translateX = Number(options.translateX) || 0;
        const translateY = Number(options.translateY) || 0;
        const rotation = Number(options.rotation) || 0;
        const feather = Math.max(0.18, Math.min(0.62, Number(options.feather) || FACE_BEAUTY_RENDERER.patchFeather));
        const opacity = Math.max(0, Math.min(1, Number.isFinite(options.opacity) ? Number(options.opacity) : 1));
        const destinationCenter = {
            x: center.x + translateX,
            y: center.y + translateY
        };
        const overscan = 1.08;
        const left = destinationCenter.x - radiusX * overscan;
        const top = destinationCenter.y - radiusY * overscan;
        const patchWidth = Math.max(2, Math.ceil(radiusX * overscan * 2));
        const patchHeight = Math.max(2, Math.ceil(radiusY * overscan * 2));
        const contextScale = Math.max(1, Math.min(2, Math.abs(context.getTransform().a) || 1));
        const pixelWidth = Math.max(2, Math.ceil(patchWidth * contextScale));
        const pixelHeight = Math.max(2, Math.ceil(patchHeight * contextScale));

        if (!this.facePatchCanvas) this.facePatchCanvas = document.createElement('canvas');
        const patchCanvas = this.facePatchCanvas;
        if (patchCanvas.width !== pixelWidth || patchCanvas.height !== pixelHeight) {
            patchCanvas.width = pixelWidth;
            patchCanvas.height = pixelHeight;
        }
        const patchContext = patchCanvas.getContext('2d');
        if (!patchContext) return;

        const coverScale = Math.max(
            previewWidth / video.videoWidth,
            previewHeight / video.videoHeight
        );
        const renderedWidth = video.videoWidth * coverScale;
        const renderedHeight = video.videoHeight * coverScale;
        const offsetX = (previewWidth - renderedWidth) / 2;
        const offsetY = (previewHeight - renderedHeight) / 2;

        patchContext.setTransform(contextScale, 0, 0, contextScale, 0, 0);
        patchContext.clearRect(0, 0, patchWidth, patchHeight);
        patchContext.save();
        patchContext.translate(-left, -top);
        patchContext.translate(destinationCenter.x, destinationCenter.y);
        patchContext.rotate(rotation);
        patchContext.scale(scaleX, scaleY);
        patchContext.rotate(-rotation);
        patchContext.translate(-center.x, -center.y);
        patchContext.translate(previewWidth, 0);
        patchContext.scale(-1, 1);
        patchContext.filter = options.filter || this.previewFilter;
        patchContext.imageSmoothingEnabled = true;
        patchContext.imageSmoothingQuality = 'high';
        patchContext.drawImage(video, offsetX, offsetY, renderedWidth, renderedHeight);
        patchContext.restore();

        patchContext.save();
        patchContext.globalCompositeOperation = 'destination-in';
        patchContext.translate(destinationCenter.x - left, destinationCenter.y - top);
        patchContext.rotate(rotation);
        patchContext.scale(radiusX, radiusY);
        const mask = patchContext.createRadialGradient(0, 0, 0, 0, 0, 1);
        mask.addColorStop(0, 'rgba(0, 0, 0, 1)');
        mask.addColorStop(1 - feather, 'rgba(0, 0, 0, 1)');
        mask.addColorStop(1, 'rgba(0, 0, 0, 0)');
        patchContext.fillStyle = mask;
        patchContext.beginPath();
        patchContext.arc(0, 0, 1, 0, Math.PI * 2);
        patchContext.fill();
        patchContext.restore();

        context.save();
        context.globalAlpha *= opacity;
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        context.drawImage(patchCanvas, 0, 0, pixelWidth, pixelHeight, left, top, patchWidth, patchHeight);
        context.restore();
    }

    private drawFaceMapCaptureEffects(
        context: CanvasRenderingContext2D,
        video: HTMLVideoElement,
        width: number,
        height: number
    ) {
        if (this.showOriginal) return;
        const liveLandmarks = this.faceSmoothedLandmarks || this.faceTargetLandmarks;
        if (!liveLandmarks) return;
        const mappedLandmarks = this.applyFaceMapOffsets(liveLandmarks);
        const mapPoint = (index: number) => this.mapLandmarkToPreview(
            mappedLandmarks[index],
            width,
            height,
            video.videoWidth,
            video.videoHeight
        );
        this.drawFaceMapRegionEffects(context, video, width, height, mapPoint, this.faceMapReady);
    }

    private midpoint(
        first: { x: number; y: number },
        second: { x: number; y: number }
    ): { x: number; y: number } {
        return {
            x: (first.x + second.x) / 2,
            y: (first.y + second.y) / 2
        };
    }

    private lerpPoint(
        first: { x: number; y: number },
        second: { x: number; y: number },
        amount: number
    ): { x: number; y: number } {
        const blend = Math.max(0, Math.min(1, Number(amount) || 0));
        return {
            x: first.x + (second.x - first.x) * blend,
            y: first.y + (second.y - first.y) * blend
        };
    }

    private pointDistance(
        first: { x: number; y: number },
        second: { x: number; y: number }
    ): number {
        return Math.hypot(second.x - first.x, second.y - first.y);
    }

    private faceMapPointColor(region: FaceMapRegion): string {
        switch (region) {
            case 'eyes':
            case 'eyebrows':
                return '#d8c9f2';
            case 'nose':
                return '#efc29e';
            case 'lips':
                return '#efb4bb';
            default:
                return '#bde0cd';
        }
    }

    private mapLandmarkToPreview(
        landmark: any,
        previewWidth: number,
        previewHeight: number,
        videoWidth: number,
        videoHeight: number
    ): { x: number; y: number } | null {
        if (!landmark || !Number.isFinite(landmark.x) || !Number.isFinite(landmark.y)) return null;

        const coverScale = Math.max(previewWidth / videoWidth, previewHeight / videoHeight);
        const renderedWidth = videoWidth * coverScale;
        const renderedHeight = videoHeight * coverScale;
        const offsetX = (previewWidth - renderedWidth) / 2;
        const offsetY = (previewHeight - renderedHeight) / 2;
        const sourceX = offsetX + landmark.x * renderedWidth;

        return {
            x: previewWidth - sourceX,
            y: offsetY + landmark.y * renderedHeight
        };
    }

    private drawLandmarkPath(
        context: CanvasRenderingContext2D,
        indices: number[],
        mapPoint: (index: number) => { x: number; y: number } | null,
        strokeStyle: string,
        lineWidth: number,
        closePath: boolean
    ) {
        const points = indices.map(mapPoint).filter((point): point is { x: number; y: number } => !!point);
        if (points.length < 2) return;

        context.beginPath();
        context.moveTo(points[0].x, points[0].y);
        points.slice(1).forEach((point) => context.lineTo(point.x, point.y));
        if (closePath) context.closePath();
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.strokeStyle = strokeStyle;
        context.lineWidth = lineWidth;
        context.stroke();
    }

    private setFaceTrackerState(state: FaceTrackerState, reason: string) {
        if (this.faceTrackerState === state) return;
        this.faceTrackerState = state;
        console.info('[MEEO Face Spike]', {
            state,
            reason,
            faceCount: this.faceCount,
            cameraFps: this.faceCameraFps,
            inferenceFps: this.faceTrackerFps,
            overlayFps: this.faceOverlayFps,
            targetInferenceFps: this.faceEffectiveTargetFps,
            inputPrepMs: this.faceInputPrepMs,
            inferenceMs: this.faceInferenceMs,
            averageLatencyMs: this.faceAverageLatencyMs,
            overlayMs: this.faceOverlayMs,
            loadMs: this.faceLoadTimeMs,
            wasmLoadMs: this.faceWasmLoadTimeMs,
            modelLoadMs: this.faceModelLoadTimeMs,
            inferenceSize: this.faceInferenceWidth + 'x' + this.faceInferenceHeight,
            delegate: this.faceDelegate,
            backend: this.faceBackend,
            postMessageMs: this.facePostMessageMs,
            assetSource: this.faceAssetSource,
            assetRoot: this.faceAssetRoot,
            initializationState: this.faceInitializationState,
            initializationMs: this.faceInitializationElapsedMs,
            assetLoadMs: this.faceAssetLoadTimeMs,
            fallbackReason: this.faceFallbackReason,
            failureReason: this.faceFailureReason,
            packageVersion: FACE_LANDMARK_SPIKE.packageVersion,
            processing: 'browser_local_only'
        });
        if (!this.destroyed) void this.service.render();
    }

    private stopFaceTracking(nextState: FaceTrackerState) {
        this.faceTrackerGeneration++;
        this.faceAssetAbortController?.abort();
        this.faceAssetAbortController = null;
        this.clearFaceInitializationTicker();
        this.cancelFaceFrameLoop();
        if (this.faceBackend === 'worker' && !this.faceWorkerReady) {
            this.disposeFaceWorker();
            this.faceBackend = 'main-thread';
        }
        this.faceTargetLandmarks = null;
        this.faceSmoothedLandmarks = null;
        this.faceOverlayOpacity = 0;
        this.faceOverlayTargetOpacity = 0;
        this.clearFaceOverlay();
        this.resetFaceTrackerMetrics(false);
        this.faceTrackerState = nextState;
        this.faceInitializationState = this.faceTrackingDisabledByUser
            ? 'cancelled'
            : (this.isFaceInferenceBackendReady() ? 'model_ready' : 'idle');
    }

    private cancelFaceFrameLoop() {
        const video = this.cameraVideo?.nativeElement as any;
        if (this.faceVideoFrameCallback !== null && typeof video?.cancelVideoFrameCallback === 'function') {
            video.cancelVideoFrameCallback(this.faceVideoFrameCallback);
        }
        this.faceVideoFrameCallback = null;
        if (this.faceFallbackAnimationFrame !== null) {
            cancelAnimationFrame(this.faceFallbackAnimationFrame);
            this.faceFallbackAnimationFrame = null;
        }
        if (this.faceOverlayAnimationFrame !== null) {
            cancelAnimationFrame(this.faceOverlayAnimationFrame);
            this.faceOverlayAnimationFrame = null;
        }
    }

    private resolveFaceWorkerInitialization() {
        if (this.faceWorkerInitializationTimeout !== null) {
            clearTimeout(this.faceWorkerInitializationTimeout);
            this.faceWorkerInitializationTimeout = null;
        }
        const resolve = this.faceWorkerInitializationResolve;
        this.faceWorkerInitializationResolve = null;
        this.faceWorkerInitializationReject = null;
        if (resolve) resolve();
    }

    private rejectFaceWorkerInitialization(error: Error) {
        if (this.faceWorkerInitializationTimeout !== null) {
            clearTimeout(this.faceWorkerInitializationTimeout);
            this.faceWorkerInitializationTimeout = null;
        }
        const reject = this.faceWorkerInitializationReject;
        this.faceWorkerInitializationResolve = null;
        this.faceWorkerInitializationReject = null;
        if (reject) reject(error);
    }

    private async fallbackFromFaceWorker(reason: string) {
        if (
            this.faceWorkerFallbackInProgress
            || this.destroyed
            || this.faceBackend !== 'worker'
        ) return;

        this.faceWorkerFallbackInProgress = true;
        const generation = this.faceTrackerGeneration;
        this.cancelFaceFrameLoop();
        this.disposeFaceWorker();
        this.faceBackend = 'main-thread';
        this.faceFallbackReason = reason;
        this.faceInitializationState = 'fallback';
        this.setFaceTrackerState('loading', reason);
        await this.service.render();

        try {
            if (!this.faceLandmarker) await this.initializeMainThreadFaceLandmarker(generation);
            if (
                this.destroyed
                || generation !== this.faceTrackerGeneration
                || !this.cameraIsActive
            ) return;

            this.resetFaceTrackerMetrics(false);
            this.faceTrackingStartedAt = performance.now();
            this.faceInitializationState = 'model_ready';
            this.setFaceTrackerState('ready', 'main_thread_fallback_ready');
            await this.service.render();
            this.scheduleFaceFrame();
            this.scheduleFaceOverlayFrame();
        } catch (error) {
            if (this.destroyed || generation !== this.faceTrackerGeneration) return;
            this.faceFailureReason = error instanceof Error ? error.message : String(error);
            this.faceInitializationState = 'failed';
            this.setFaceTrackerState('error', 'main_thread_fallback_failed');
            this.clearFaceOverlay();
            await this.service.render();
        } finally {
            this.faceWorkerFallbackInProgress = false;
        }
    }

    private disposeFaceWorker() {
        if (this.faceWorkerInitializationTimeout !== null) {
            clearTimeout(this.faceWorkerInitializationTimeout);
            this.faceWorkerInitializationTimeout = null;
        }
        if (this.faceWorkerInitializationReject) {
            const reject = this.faceWorkerInitializationReject;
            this.faceWorkerInitializationResolve = null;
            this.faceWorkerInitializationReject = null;
            reject(new Error('Face worker was disposed'));
        } else {
            this.faceWorkerInitializationResolve = null;
        }

        if (this.faceWorker) {
            this.faceWorker.onmessage = null;
            this.faceWorker.onerror = null;
            try { this.faceWorker.postMessage({ type: 'dispose' }); } catch (error) { }
            this.faceWorker.terminate();
        }
        this.faceWorker = null;
        this.faceWorkerReady = false;
        this.faceWorkerBusy = false;
    }

    private disposeFaceLandmarker() {
        if (!this.faceLandmarker) return;
        try { this.faceLandmarker.close(); } catch (error) { }
        this.faceLandmarker = null;
        this.faceInferenceCanvas = null;
        this.faceInferenceContext = null;
        this.facePatchCanvas = null;
    }

    private disposeFaceInferenceBackend() {
        this.faceAssetAbortController?.abort();
        this.faceAssetAbortController = null;
        this.clearFaceInitializationTicker();
        this.disposeFaceWorker();
        this.disposeFaceLandmarker();
        this.facePatchCanvas = null;
        this.faceBackend = 'main-thread';
        this.faceWorkerFallbackInProgress = false;
    }

    private clearFaceOverlay() {
        const canvas = this.landmarkCanvas?.nativeElement;
        const context = canvas?.getContext('2d');
        if (canvas && context) context.clearRect(0, 0, canvas.width, canvas.height);
    }

    private resetFaceTrackerMetrics(resetInitialization: boolean = true) {
        this.faceTrackerFps = 0;
        this.faceCameraFps = 0;
        this.faceOverlayFps = 0;
        this.faceInferenceMs = 0;
        this.faceInputPrepMs = 0;
        this.facePostMessageMs = 0;
        this.faceAverageLatencyMs = 0;
        this.faceOverlayMs = 0;
        if (resetInitialization) {
            this.faceLoadTimeMs = 0;
            this.faceWasmLoadTimeMs = 0;
            this.faceModelLoadTimeMs = 0;
            this.faceAssetLoadTimeMs = 0;
            this.faceInitializationElapsedMs = 0;
            this.faceDelegate = 'unknown';
        }
        this.faceCount = 0;
        this.faceEffectiveTargetFps = this.faceTargetFps;
        this.faceInferenceWidth = 0;
        this.faceInferenceHeight = 0;
        this.faceLastVideoTime = -1;
        this.faceLastDetectionAt = 0;
        this.faceTrackingStartedAt = 0;
        this.faceLastUiUpdateAt = 0;
        this.faceLastOverlayAt = 0;
        this.faceLastPoseCheckAt = 0;
        this.faceRotationOutsideRange = false;
        this.faceLastPresentedFrames = null;
        this.faceLastMediaTime = null;
        this.faceTargetLandmarks = null;
        this.faceSmoothedLandmarks = null;
        this.faceOverlayAllowsEffects = true;
        this.faceOverlayOpacity = 0;
        this.faceOverlayTargetOpacity = 0;
        this.faceDetectionTimes = [];
        this.faceCameraFpsSamples = [];
        this.faceCameraFrameTimes = [];
        this.faceOverlayTimes = [];
        this.faceInferenceDurations = [];
        this.faceInputPrepDurations = [];
        this.facePostMessageDurations = [];
        this.faceLatencyDurations = [];
        this.faceOverlayDurations = [];
    }

    public get activeCount(): number {
        return this.filters.length;
    }

    public get previewFilter(): string {
        if (this.showOriginal || this.filters.length === 0) return 'none';

        const globalStrength = this.globalIntensity / 100;
        const softLight = this.filterStrength('soft-light', 'natural-light');
        const editorial = this.filterStrength('editorial-light');
        const warm = this.filterStrength('warm-light');
        const film = this.filterStrength('film-camera', 'film-grain');

        const brightness = 1 + globalStrength * ((softLight * 0.08) + (editorial * 0.04) + (warm * 0.025));
        const contrast = 1 + globalStrength * (editorial * 0.10);
        const saturation = 1 + globalStrength * ((warm * 0.06) + (editorial * 0.04));
        const sepia = globalStrength * film * 0.055;

        return 'brightness(' + brightness.toFixed(3) + ') contrast(' + contrast.toFixed(3) + ') saturate(' + saturation.toFixed(3) + ') sepia(' + sepia.toFixed(3) + ')';
    }

    public get tintOpacity(): number {
        if (this.showOriginal || this.filters.length === 0) return 0;
        const warmth = this.filterStrength('warm-light', 'film-camera', 'editorial-light');
        return warmth * 0.08 * (this.globalIntensity / 100);
    }

    public get grainOpacity(): number {
        if (this.showOriginal || !this.hasFilter('film-grain')) return 0;
        const grain = this.filters.find((item) => item.id === 'film-grain');
        return (grain ? grain.value / 100 : 0) * 0.16 * (this.globalIntensity / 100);
    }

    public hasFilter(id: string): boolean {
        return this.filters.some((item) => item.id === id);
    }

    private filterStrength(...ids: string[]): number {
        return this.filters.reduce((strength, item) => {
            if (!ids.includes(item.id)) return strength;
            const value = Math.max(0, Math.min(100, Number(item.value) || 0)) / 100;
            return Math.max(strength, value);
        }, 0);
    }

    private faceEffectStrength(...ids: string[]): number {
        const globalStrength = Math.max(0, Math.min(1, this.globalIntensity / 100));
        const rawStrength = this.filterStrength(...ids) * globalStrength;
        if (rawStrength <= 0) return 0;
        return Math.min(1, Math.pow(rawStrength, FACE_BEAUTY_RENDERER.strengthExponent));
    }

    private composeCanvasFilter(baseFilter: string, localFilter: string): string {
        return [baseFilter, localFilter]
            .filter((value) => !!value && value !== 'none')
            .join(' ') || 'none';
    }

    private filterDirection(id: string): number {
        const filter = this.filters.find((item) => item.id === id);
        return Number(filter?.direction) < 0 ? -1 : 1;
    }

    private hasFaceFilters(): boolean {
        return this.filters.some((item) => String(item.path || '').startsWith('face.'));
    }

    public async selectPrompt(text: string) {
        this.prompt = text;
        await this.service.render();
        await this.submitPrompt();
    }

    private matchFaceMapCommand(text: string): FaceMapCommand | null {
        const matched = FACE_MAP_COMMANDS.find((command) => command.pattern.test(text));
        return matched || null;
    }

    private applyFaceMapCommand(command: FaceMapCommand) {
        this.pushUndo();
        const adjustments: FaceMapFilterCommand[] = [command, ...(command.companions || [])];
        adjustments.forEach((adjustment) => {
            const nextFilter = {
                ...this.makeFilter(
                    adjustment.filterId,
                    'FaceMap',
                    adjustment.label,
                    adjustment.path,
                    adjustment.value,
                    adjustment.max,
                    adjustment.tone
                ),
                faceMapRegion: adjustment.region,
                direction: Number(adjustment.direction) < 0 ? -1 : 1
            };
            this.upsertFilter(nextFilter);
        });
        this.selectedFilterId = command.filterId;
        this.policyDecision = 'allow';
        this.operation = 'patch';
        this.planId = 'facemap_' + command.region + '_' + String(Date.now()).slice(-8);
        this.filterPlanSource = 'local_facemap';
        this.filterPlanModel = '';
        this.filterPlanFallbackReason = '';
        this.facePlanWaitingForMap = !this.faceMapReady;
        this.assistantMessage = this.facePlanWaitingForMap
            ? 'FilterPlan과 슬라이더를 준비했어요. 실제 변화는 FaceMap 인식을 완료한 뒤 적용돼요.'
            : command.response;
        this.safeOptions = [];
        this.showOriginal = false;
    }

    public async submitPrompt() {
        const text = (this.prompt || '').trim();
        if (!text || this.isInterpreting) return;

        this.lastUserMessage = text;
        this.prompt = '';
        this.safeOptions = [];
        this.isInterpreting = true;
        const token = ++this.requestToken;
        await this.service.render();

        try {
            const response = await wiz.call(FILTER_PLAN_API.action, {
                prompt: text,
                request_id: token,
                context: JSON.stringify(this.compactFilterContext())
            });

            if (token !== this.requestToken) return;

            const responseData = response?.data?.status
                ? response.data
                : response?.data?.data;

            if (response?.code !== 200 || !responseData) {
                this.applyClientFallback(text, 'api_request_failed');
            } else {
                this.setFilterPlanMeta(responseData);
                this.applyInterpretationResult(responseData, text, token);
            }
        } catch (error) {
            if (token !== this.requestToken) return;
            this.applyClientFallback(text, 'network_error');
        }

        if (token !== this.requestToken) return;
        this.isInterpreting = false;
        await this.service.render();
    }

    public async cancelInterpretation() {
        this.requestToken++;
        this.isInterpreting = false;
        this.assistantMessage = '해석을 멈췄어요. 현재 무드는 그대로예요.';
        await this.service.render();
    }

    public async chooseSafeOption(option: string) {
        this.safeOptions = [];
        if (option === '그대로 두기') {
            this.policyDecision = 'allow';
            this.assistantMessage = '현재 무드를 그대로 유지할게요.';
            await this.service.render();
            return;
        }

        this.prompt = option;
        await this.service.render();
        await this.submitPrompt();
    }

    private isDemeaningRequest(text: string): boolean {
        return /(못생|추해|결점|돼지|뚱뚱|고쳐\s*줘|ugly)/i.test(text);
    }

    private isProtectedTraitRequest(text: string): boolean {
        return /(다른\s*인종|백인처럼|흑인처럼|동양인처럼|서양인처럼|피부.{0,5}(하얗|어둡|검게))/i.test(text);
    }

    private isGeometryRequest(text: string): boolean {
        return /(얼굴.{0,6}(작게|줄여)|다른\s*사람처럼|성형|워핑)/i.test(text);
    }

    private compactFilterContext(): any {
        return {
            activeFilters: this.filters.slice(0, 16).map((item) => ({
                id: String(item.id || ''),
                value: Math.max(0, Math.min(100, Math.round(Number(item.value) || 0))),
                enumValue: String(item.enumValue || '')
            })),
            globalIntensity: Math.max(0, Math.min(100, Math.round(this.globalIntensity))),
            operation: ['patch', 'replace', 'reset'].includes(this.operation) ? this.operation : 'patch'
        };
    }

    private applyInterpretationResult(payload: any, text: string, token: number) {
        if (Number(payload.requestId) !== token) return;

        if (payload.status === 'ready') {
            if (!this.applyServerPlan(payload.plan, token, text)) {
                this.applyClientFallback(text, 'invalid_response');
                return;
            }
            this.showOriginal = false;
            return;
        }

        if (['clarify', 'reframed', 'blocked'].includes(payload.status)) {
            this.policyDecision = payload.status === 'blocked' ? 'block' : 'reframe';
            this.operation = 'patch';
            this.assistantMessage = String(payload.message || '현재 무드는 유지했어요.');
            this.safeOptions = Array.isArray(payload.suggestions)
                ? payload.suggestions.slice(0, 3).map((item) => String(item))
                : [];
            return;
        }

        this.applyClientFallback(text, 'invalid_response');
    }

    private setFilterPlanMeta(payload: any) {
        const meta = payload?.meta && typeof payload.meta === 'object'
            ? payload.meta
            : {};
        this.filterPlanSource = this.safeMetaValue(meta.source, 'unknown');
        this.filterPlanModel = this.safeMetaValue(meta.model);
        this.filterPlanFallbackReason = this.safeMetaValue(meta.fallbackReason);
    }

    private safeMetaValue(value: any, fallback: string = ''): string {
        if (typeof value !== 'string') return fallback;
        const normalized = value.trim();
        return normalized ? normalized.slice(0, 80) : fallback;
    }

    private applyClientFallback(text: string, reason: string) {
        this.filterPlanSource = 'client_mock';
        this.filterPlanModel = '';
        this.filterPlanFallbackReason = reason;

        const faceMapCommand = this.matchFaceMapCommand(text);
        if (faceMapCommand) {
            this.applyFaceMapCommand(faceMapCommand);
            this.filterPlanSource = 'client_mock';
            this.filterPlanFallbackReason = reason;
            return;
        }

        if (this.isDemeaningRequest(text)) {
            this.policyDecision = 'reframe';
            this.operation = 'patch';
            this.assistantMessage = '얼굴을 평가하거나 고치지는 않아요. 원하는 조명과 카메라 무드는 함께 만들 수 있어요.';
            this.safeOptions = ['맑은 자연광', '차분한 필름 무드'];
            return;
        }

        if (this.isProtectedTraitRequest(text)) {
            this.policyDecision = 'reframe';
            this.operation = 'patch';
            this.assistantMessage = '피부톤과 정체성 특징은 바꾸지 않아요. 전체 조명이나 일반적인 필름 색감은 선택할 수 있어요.';
            this.safeOptions = ['전체 조명을 맑게', '따뜻한 필름 무드'];
            return;
        }

        if (this.isGeometryRequest(text)) {
            this.policyDecision = 'reframe';
            this.operation = 'patch';
            this.assistantMessage = '과도한 얼굴 변형은 적용하지 않아요. 원하는 얼굴 영역을 낮은 강도로 조절할 수 있어요.';
            this.safeOptions = ['눈을 살짝 크게', '콧대 하이라이트'];
            return;
        }

        this.pushUndo();
        this.applyMappedPlan(text);
        this.showOriginal = false;
    }

    private applyServerPlan(plan: any, token: number, text: string): boolean {
        if (!plan || plan.schemaVersion !== FILTER_PLAN_SCHEMA_VERSION || Number(plan.requestId) !== token) return false;
        if (!['patch', 'replace', 'reset'].includes(plan.operation)) return false;
        const planIntensity = Number(plan.globalIntensity);
        if (!Number.isFinite(planIntensity) || planIntensity < 0 || planIntensity > 1) return false;
        if (!['allow', 'allow_with_limits'].includes(plan.policy?.decision)) return false;
        if (!this.validParameters(plan.parameters)) return false;

        const parameters = plan.parameters;
        const nextFilters: any[] = [];
        const pushNumber = (
            enabled: boolean,
            rawValue: any,
            max: number,
            id: string,
            category: string,
            label: string,
            path: string,
            tone: string,
            enumValue: string = '',
            faceMapRegion: FaceMapRegion | '' = ''
        ) => {
            const numeric = Number(rawValue);
            if (!enabled || !Number.isFinite(numeric) || Math.abs(numeric) < 0.0001) return;
            const value = Math.round(Math.min(1, Math.abs(numeric) / max) * 100);
            const filter = this.makeFilter(id, category, label, path, value, max, tone, enumValue);
            filter.direction = numeric < 0 ? -1 : 1;
            if (faceMapRegion) filter.faceMapRegion = faceMapRegion;
            nextFilters.push(filter);
        };

        pushNumber(parameters.lighting.enabled, parameters.lighting.exposure, 0.35, 'natural-light', '조명', parameters.lighting.exposure >= 0 ? '맑은 전체 조명' : '차분한 낮은 조명', 'lighting.exposure', 'apricot');
        pushNumber(parameters.lighting.enabled, parameters.lighting.temperature, 0.40, parameters.lighting.temperature >= 0 ? 'warm-light' : 'cool-light', '조명', parameters.lighting.temperature >= 0 ? '따뜻한 색온도' : '차가운 색온도', 'lighting.temperature', 'apricot');
        pushNumber(parameters.lighting.enabled, parameters.lighting.contrast, 0.30, 'editorial-light', '조명', parameters.lighting.contrast >= 0 ? '또렷한 조명 대비' : '부드러운 조명 대비', 'lighting.contrast', 'apricot');
        pushNumber(parameters.lighting.enabled, parameters.lighting.highlightRecovery, 0.60, 'soft-light', '조명', '부드러운 하이라이트', 'lighting.highlightRecovery', 'apricot');

        pushNumber(parameters.makeup.enabled, parameters.makeup.lipTint.amount, 0.65, 'lip-tint', '메이크업', '립 틴트', 'makeup.lipTint.amount', 'lilac', parameters.makeup.lipTint.tone);
        pushNumber(parameters.makeup.enabled, parameters.makeup.blush.amount, 0.55, 'blush', '메이크업', '은은한 블러시', 'makeup.blush.amount', 'apricot', parameters.makeup.blush.tone);
        pushNumber(parameters.makeup.enabled, parameters.makeup.eyeDefinition, 0.45, 'eye-definition', '메이크업', '눈 주변 색 대비', 'makeup.eyeDefinition', 'lilac');

        pushNumber(parameters.contour.enabled, parameters.contour.sculptLight, 0.35, 'sculpt-light', '빛 윤곽', '자연스러운 턱선 빛', 'contour.sculptLight', 'sage');

        pushNumber(parameters.atmosphere.enabled, parameters.atmosphere.glow, 0.60, 'calm-glow', '분위기', '은은한 글로우', 'atmosphere.glow', 'lilac');
        pushNumber(parameters.atmosphere.enabled, parameters.atmosphere.grain, 0.50, 'film-grain', '분위기', '잔잔한 필름 입자', 'atmosphere.grain', 'sand');
        pushNumber(parameters.atmosphere.enabled, parameters.atmosphere.vignette, 0.45, 'vignette', '분위기', '부드러운 비네트', 'atmosphere.vignette', 'sand');

        if (parameters.camera.enabled && (parameters.camera.palette !== 'clean-neutral' || parameters.camera.softFocus > 0)) {
            const cameraValue = Math.max(
                parameters.camera.palette !== 'clean-neutral' ? 30 : 0,
                Math.round((parameters.camera.softFocus / 0.45) * 100)
            );
            const paletteLabels: any = {
                'warm-film': '웜 필름 팔레트',
                'cool-film': '쿨 필름 팔레트',
                'night-soft': '나이트 소프트 팔레트',
                'monochrome': '모노크롬 팔레트'
            };
            nextFilters.push(this.makeFilter(
                'film-camera',
                '카메라',
                paletteLabels[parameters.camera.palette] || '카메라 팔레트',
                'camera.softFocus',
                cameraValue,
                0.45,
                'sage',
                parameters.camera.palette
            ));
        }
        pushNumber(parameters.camera.enabled, parameters.camera.sharpness, 0.35, 'camera-sharpness', '카메라', parameters.camera.sharpness >= 0 ? '선명한 카메라 질감' : '부드러운 카메라 질감', 'camera.sharpness', 'sage');

        pushNumber(parameters.face.enabled, parameters.face.eyes.size, 0.12, 'face-eyes-scale', 'FaceMap', '눈 영역 확대', 'face.eyes.size', 'lilac', '', 'eyes');
        pushNumber(parameters.face.enabled, parameters.face.nose.highlight, 0.30, 'face-nose-highlight', 'FaceMap', '콧대 하이라이트', 'face.nose.highlight', 'sand', '', 'nose');
        pushNumber(parameters.face.enabled, parameters.face.jaw.width, 0.14, 'face-jaw-width', 'FaceMap', '턱선 너비', 'face.jaw.width', 'sage', '', 'jaw');
        pushNumber(parameters.face.enabled, parameters.face.jaw.vline, 0.12, 'face-jaw-vline', 'FaceMap', 'V라인', 'face.jaw.vline', 'sage', '', 'jaw');
        pushNumber(parameters.face.enabled, parameters.face.chin.taper, 0.10, 'face-chin-taper', 'FaceMap', '턱끝 테이퍼', 'face.chin.taper', 'sage', '', 'jaw');
        pushNumber(parameters.face.enabled, parameters.face.lips.volume, 0.18, 'face-lips-volume', 'FaceMap', '입술 볼륨', 'face.lips.volume', 'apricot', '', 'lips');
        pushNumber(parameters.face.enabled, parameters.face.brows.lift, 0.10, 'face-brows-lift', 'FaceMap', '눈썹 리프트', 'face.brows.lift', 'lilac', '', 'eyebrows');

        const previousFilterIds = new Set(this.filters.map((item) => String(item.id || '')));
        this.pushUndo();
        if (plan.operation === 'reset') {
            this.filters = [];
            this.selectedFilterId = '';
        } else {
            this.filters = this.mergeFilters(nextFilters);
            const faceCommand = this.matchFaceMapCommand(text);
            const requestedFilterId = faceCommand?.filterId || '';
            const addedFilter = nextFilters.find((item) => !previousFilterIds.has(String(item.id || '')));
            if (requestedFilterId && this.hasFilter(requestedFilterId)) {
                this.selectedFilterId = requestedFilterId;
            } else if (addedFilter) {
                this.selectedFilterId = addedFilter.id;
            }
        }
        if (this.shouldApplyPlanIntensity(plan.operation, text)) {
            this.globalIntensity = Math.round(planIntensity * 100);
        }
        this.policyDecision = String(plan.policy?.decision || 'allow');
        this.operation = plan.operation === 'reset' ? 'reset' : 'patch';
        this.planId = String(plan.planId || ('plan_' + String(Date.now()).slice(-8)));
        this.assistantMessage = String(plan.assistantMessage || '요청한 무드를 적용했어요.');
        this.facePlanWaitingForMap = this.hasFaceFilters() && !this.faceMapReady;
        if (this.facePlanWaitingForMap) {
            this.assistantMessage = 'FilterPlan과 슬라이더를 준비했어요. 실제 얼굴 변화는 FaceMap 인식을 완료한 뒤 로컬에서 적용돼요.';
        }
        this.safeOptions = [];
        return true;
    }

    private validParameters(parameters: any): boolean {
        if (!parameters || typeof parameters !== 'object') return false;
        const requiredCategories = ['lighting', 'makeup', 'contour', 'atmosphere', 'camera', 'face'];
        if (!requiredCategories.every((category) => parameters[category] && typeof parameters[category] === 'object')) return false;

        const finiteWithin = (value: any, min: number, max: number) => {
            const numeric = Number(value);
            return Number.isFinite(numeric) && numeric >= min && numeric <= max;
        };

        return typeof parameters.lighting.enabled === 'boolean'
            && finiteWithin(parameters.lighting.exposure, -0.35, 0.35)
            && finiteWithin(parameters.lighting.temperature, -0.40, 0.40)
            && finiteWithin(parameters.lighting.contrast, -0.30, 0.30)
            && finiteWithin(parameters.lighting.highlightRecovery, 0, 0.60)
            && typeof parameters.makeup.enabled === 'boolean'
            && parameters.makeup.lipTint
            && parameters.makeup.blush
            && ['soft-rose', 'coral', 'berry', 'classic-red', 'mauve'].includes(parameters.makeup.lipTint.tone)
            && ['rose', 'coral', 'mauve', 'berry'].includes(parameters.makeup.blush.tone)
            && finiteWithin(parameters.makeup.lipTint.amount, 0, 0.65)
            && finiteWithin(parameters.makeup.blush.amount, 0, 0.55)
            && finiteWithin(parameters.makeup.eyeDefinition, 0, 0.45)
            && typeof parameters.contour.enabled === 'boolean'
            && finiteWithin(parameters.contour.sculptLight, 0, 0.35)
            && typeof parameters.atmosphere.enabled === 'boolean'
            && finiteWithin(parameters.atmosphere.glow, 0, 0.60)
            && finiteWithin(parameters.atmosphere.grain, 0, 0.50)
            && finiteWithin(parameters.atmosphere.vignette, 0, 0.45)
            && typeof parameters.camera.enabled === 'boolean'
            && ['clean-neutral', 'warm-film', 'cool-film', 'night-soft', 'monochrome'].includes(parameters.camera.palette)
            && finiteWithin(parameters.camera.sharpness, -0.35, 0.35)
            && finiteWithin(parameters.camera.softFocus, 0, 0.45)
            && typeof parameters.face.enabled === 'boolean'
            && parameters.face.eyes
            && parameters.face.nose
            && parameters.face.jaw
            && parameters.face.chin
            && parameters.face.lips
            && parameters.face.brows
            && finiteWithin(parameters.face.eyes.size, 0, 0.12)
            && finiteWithin(parameters.face.nose.highlight, 0, 0.30)
            && finiteWithin(parameters.face.jaw.width, -0.14, 0)
            && finiteWithin(parameters.face.jaw.vline, 0, 0.12)
            && finiteWithin(parameters.face.chin.taper, 0, 0.10)
            && finiteWithin(parameters.face.lips.volume, 0, 0.18)
            && finiteWithin(parameters.face.brows.lift, 0, 0.10);
    }

    private applyMappedPlan(text: string) {
        const normalized = text.replace(/\s/g, '');
        this.policyDecision = 'allow';
        this.planId = 'plan_' + String(Date.now()).slice(-8);

        if (/화보/.test(normalized)) {
            this.operation = 'replace';
            this.filters = [
                this.makeFilter('editorial-light', '조명', '화보 조명', 'lighting.contrast', 48, 0.30, 'apricot'),
                this.makeFilter('editorial-glow', '분위기', '은은한 글로우', 'atmosphere.glow', 46, 0.60, 'lilac'),
                this.makeFilter('film-camera', '카메라', '웜 필름 팔레트', 'camera.softFocus', 28, 0.45, 'sage', 'warm-film'),
                this.makeFilter('film-grain', '분위기', '잔잔한 필름 입자', 'atmosphere.grain', 34, 0.50, 'sand')
            ];
            this.assistantMessage = '화보처럼 정돈된 빛과 은은한 글로우, 따뜻한 필름 질감을 담았어요.';
            return;
        }

        this.operation = 'patch';

        if (/((턱선|하관).{0,8}(빛|조명|음영|그림자|컨투어|쉐이딩)|(빛|조명|음영|그림자).{0,8}(턱선|하관)|빛으로또렷)/.test(normalized)) {
            this.upsertFilter(this.makeFilter('sculpt-light', '빛 윤곽', '자연스러운 턱선 빛', 'contour.sculptLight', 52, 0.35, 'sage'));
            this.assistantMessage = '얼굴 형태는 그대로 두고, 턱 주변에 낮은 강도의 빛과 음영만 더했어요.';
            return;
        }

        if (/눈매|눈주변/.test(normalized)) {
            this.upsertFilter(this.makeFilter('eye-definition', '메이크업', '눈 주변 색 대비', 'makeup.eyeDefinition', 58, 0.45, 'lilac'));
            this.assistantMessage = '눈의 크기나 형태는 바꾸지 않고, 눈 주변 색 대비만 또렷하게 했어요.';
            return;
        }

        if (/조명만부드럽|전체조명을맑게|맑은자연광/.test(normalized)) {
            this.upsertFilter(this.makeFilter('soft-light', '조명', '부드러운 자연광', 'lighting.highlightRecovery', 54, 0.60, 'apricot'));
            this.assistantMessage = '다른 무드는 유지하고, 전체 조명의 밝은 영역만 부드럽게 정돈했어요.';
            return;
        }

        if (/따뜻/.test(normalized)) {
            this.upsertFilter(this.makeFilter('warm-light', '조명', '따뜻한 색온도', 'lighting.temperature', 55, 0.40, 'apricot'));
        }

        if (/필름/.test(normalized)) {
            this.upsertFilter(this.makeFilter('film-camera', '카메라', '웜 필름 팔레트', 'camera.softFocus', 24, 0.45, 'sage', 'warm-film'));
            this.upsertFilter(this.makeFilter('film-grain', '분위기', '잔잔한 필름 입자', 'atmosphere.grain', 30, 0.50, 'sand'));
        }

        if (/차분/.test(normalized)) {
            this.upsertFilter(this.makeFilter('calm-glow', '분위기', '차분한 글로우', 'atmosphere.glow', 26, 0.60, 'lilac'));
        }

        if (/밝/.test(normalized)) {
            this.upsertFilter(this.makeFilter('natural-light', '조명', '맑은 전체 조명', 'lighting.exposure', 42, 0.35, 'apricot'));
        }

        if (this.filters.length === 0) {
            this.upsertFilter(this.makeFilter('calm-glow', '분위기', '잔잔한 무드', 'atmosphere.glow', 22, 0.60, 'lilac'));
            this.upsertFilter(this.makeFilter('natural-light', '조명', '맑은 전체 조명', 'lighting.exposure', 28, 0.35, 'apricot'));
        }

        this.assistantMessage = '말해준 분위기를 빛과 카메라 질감으로 옮겼어요. 각 강도는 바로 조절할 수 있어요.';
    }

    private makeFilter(id: string, category: string, label: string, path: string, value: number, max: number, tone: string, enumValue: string = ''): any {
        return { id, category, label, path, value, max, tone, enumValue };
    }

    private upsertFilter(next: any) {
        const index = this.filters.findIndex((item) => (
            item.id === next.id
            || (!!item.path && !!next.path && item.path === next.path)
        ));
        if (index === -1) {
            this.filters = [...this.filters, next];
        } else {
            const copy = [...this.filters];
            copy[index] = { ...copy[index], ...next };
            this.filters = copy;
        }
    }

    private mergeFilters(nextFilters: any[]): any[] {
        return nextFilters.reduce((merged, next) => {
            const index = merged.findIndex((item: any) => (
                item.id === next.id
                || (!!item.path && !!next.path && item.path === next.path)
            ));
            if (index === -1) return [...merged, next];

            const copy = [...merged];
            copy[index] = { ...copy[index], ...next };
            return copy;
        }, this.filters.map((item) => ({ ...item })));
    }

    private shouldApplyPlanIntensity(operation: string, text: string): boolean {
        if (operation === 'reset') return true;
        const normalized = String(text || '').replace(/\s/g, '');
        return /(전체|무드|필터).{0,8}(강도|세기)|(강도|세기).{0,8}(전체|무드|필터)/i.test(normalized);
    }

    public async selectFilter(id: string) {
        if (!this.hasFilter(id)) return;
        this.selectedFilterId = id;
        await this.service.render();
    }

    public async removeFilter(id: string) {
        const index = this.filters.findIndex((item) => item.id === id);
        if (index === -1) return;

        this.pushUndo();
        const remainingFilters = this.filters.filter((item) => item.id !== id);
        this.filters = remainingFilters;
        if (this.selectedFilterId === id) {
            this.selectedFilterId = remainingFilters[Math.min(index, remainingFilters.length - 1)]?.id || '';
        }
        this.operation = 'patch';
        this.planId = 'plan_' + String(Date.now()).slice(-8);
        this.facePlanWaitingForMap = this.hasFaceFilters() && !this.faceMapReady;
        this.assistantMessage = '선택한 필터를 삭제했어요. 다른 필터는 그대로 유지돼요.';
        await this.service.render();
        this.showToast('필터를 삭제했어요. 되돌리기로 복원할 수 있어요.');
    }

    public parameterValue(filter: any): string {
        const direction = Number(filter.direction) < 0 ? -1 : 1;
        const value = direction * (Number(filter.value) / 100) * Number(filter.max);
        return value.toFixed(2);
    }

    public startAdjustment() {
        if (!this.adjustmentStart) this.adjustmentStart = this.snapshot();
    }

    public async setFilterValue(id: string, event: any) {
        this.startAdjustment();
        const value = Math.max(0, Math.min(100, Number(event.target.value) || 0));
        this.filters = this.filters.map((item) => item.id === id ? { ...item, value } : item);
        this.operation = 'patch';
        this.planId = 'plan_' + String(Date.now()).slice(-8);
        await this.service.render();
    }

    public async setGlobalIntensity(event: any) {
        this.startAdjustment();
        this.globalIntensity = Math.max(0, Math.min(100, Number(event.target.value) || 0));
        await this.service.render();
    }

    public async endAdjustment() {
        if (!this.adjustmentStart) return;
        this.undoStack.push(this.adjustmentStart);
        this.undoStack = this.undoStack.slice(-20);
        this.redoStack = [];
        this.adjustmentStart = null;
        this.assistantMessage = '강도를 조절했어요. 현재 무드는 언제든 되돌릴 수 있어요.';
        await this.service.render();
    }

    private snapshot(): any {
        return {
            filters: JSON.parse(JSON.stringify(this.filters)),
            globalIntensity: this.globalIntensity,
            assistantMessage: this.assistantMessage,
            policyDecision: this.policyDecision,
            operation: this.operation,
            planId: this.planId,
            lastUserMessage: this.lastUserMessage,
            selectedFilterId: this.selectedFilterId
        };
    }

    private restore(snapshot: any) {
        this.filters = JSON.parse(JSON.stringify(snapshot.filters || []));
        this.globalIntensity = snapshot.globalIntensity;
        this.assistantMessage = snapshot.assistantMessage;
        this.policyDecision = snapshot.policyDecision;
        this.operation = snapshot.operation;
        this.planId = snapshot.planId;
        this.lastUserMessage = snapshot.lastUserMessage;
        this.selectedFilterId = this.hasFilter(snapshot.selectedFilterId)
            ? snapshot.selectedFilterId
            : '';
        this.showOriginal = false;
        this.safeOptions = [];
        this.facePlanWaitingForMap = this.hasFaceFilters() && !this.faceMapReady;
    }

    private pushUndo() {
        this.undoStack.push(this.snapshot());
        this.undoStack = this.undoStack.slice(-20);
        this.redoStack = [];
    }

    public async undo() {
        if (!this.undoStack.length) return;
        this.redoStack.push(this.snapshot());
        const previous = this.undoStack.pop();
        this.restore(previous);
        this.showToast('직전 무드로 돌아왔어요.');
        await this.service.render();
    }

    public async redo() {
        if (!this.redoStack.length) return;
        this.undoStack.push(this.snapshot());
        const next = this.redoStack.pop();
        this.restore(next);
        this.showToast('다시 적용했어요.');
        await this.service.render();
    }

    public async resetMood() {
        if (!this.filters.length) {
            this.showToast('이미 원본 상태예요.');
            return;
        }
        this.pushUndo();
        this.filters = [];
        this.selectedFilterId = '';
        this.globalIntensity = 60;
        this.operation = 'reset';
        this.policyDecision = 'allow';
        this.planId = 'plan_original';
        this.showOriginal = false;
        this.facePlanWaitingForMap = false;
        this.safeOptions = [];
        this.assistantMessage = '모든 무드를 초기화했어요. 원본 상태로 돌아왔어요.';
        await this.service.render();
        this.showToast('초기화했어요. 되돌리기로 복원할 수 있어요.');
    }

    public async setComparison(original: boolean) {
        this.showOriginal = original;
        if (original) this.showFaceRecognitionPoints = false;
        await this.service.render();
    }

    public async togglePlan() {
        this.planOpen = !this.planOpen;
        await this.service.render();
    }

    public async capture() {
        const video = this.cameraVideo?.nativeElement;
        const canvas = this.captureCanvas?.nativeElement;

        if (this.isCapturing) return;

        if (!this.cameraIsActive || !this.mediaStream?.active || !video || !canvas || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
            this.showToast('카메라가 켜지고 영상이 보일 때 촬영할 수 있어요.');
            return;
        }

        this.isCapturing = true;
        this.captureFlash = true;
        await this.service.render();
        await this.service.sleep(130);

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');

        if (!context) {
            this.captureFlash = false;
            this.isCapturing = false;
            this.showToast('촬영 프레임을 만들지 못했어요. 다시 시도해주세요.');
            await this.service.render();
            return;
        }

        context.save();
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
        context.filter = this.previewFilter;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        context.restore();

        this.drawFaceMapCaptureEffects(context, video, canvas.width, canvas.height);

        if (this.tintOpacity > 0) {
            context.fillStyle = 'rgba(233, 169, 119, ' + Math.min(this.tintOpacity, 0.14).toFixed(3) + ')';
            context.fillRect(0, 0, canvas.width, canvas.height);
        }

        this.drawFilmGrain(context, canvas.width, canvas.height, this.grainOpacity);

        try {
            this.capturedFrame = canvas.toDataURL('image/jpeg', 0.92);
            this.capturedFilterCount = this.showOriginal ? 0 : this.activeCount;
            this.capturedIntensity = this.showOriginal ? 0 : this.globalIntensity;
            this.capturedModeLabel = this.showOriginal || !this.activeCount ? '원본' : '보정 결과';
            this.captured = true;
        } catch (error) {
            this.showToast('촬영 결과를 만들지 못했어요. 다시 시도해주세요.');
        }

        this.captureFlash = false;
        this.isCapturing = false;
        await this.service.render();
    }

    private drawFilmGrain(context: CanvasRenderingContext2D, width: number, height: number, opacity: number) {
        if (opacity <= 0 || typeof document === 'undefined') return;

        const tile = document.createElement('canvas');
        tile.width = 48;
        tile.height = 48;
        const tileContext = tile.getContext('2d');
        if (!tileContext) return;

        const pixels = tileContext.createImageData(tile.width, tile.height);
        for (let index = 0; index < pixels.data.length; index += 4) {
            const pixel = index / 4;
            const shade = (pixel * 29 + (pixel % tile.width) * 17) % 256;
            pixels.data[index] = shade;
            pixels.data[index + 1] = shade;
            pixels.data[index + 2] = shade;
            pixels.data[index + 3] = 255;
        }
        tileContext.putImageData(pixels, 0, 0);

        const pattern = context.createPattern(tile, 'repeat');
        if (!pattern) return;

        context.save();
        context.globalAlpha = Math.min(opacity, 0.16);
        context.globalCompositeOperation = 'overlay';
        context.fillStyle = pattern;
        context.fillRect(0, 0, width, height);
        context.restore();
    }

    public async closeCapture() {
        this.captured = false;
        await this.service.render();
    }

    public async saveCapture() {
        if (!this.capturedFrame) return;

        const link = document.createElement('a');
        link.href = this.capturedFrame;
        link.download = 'meeo-' + new Date().toISOString().replace(/[:.]/g, '-') + '.jpg';
        document.body.appendChild(link);
        link.click();
        link.remove();

        this.showToast('촬영한 프레임을 기기에 저장했어요.');
        this.captured = false;
        await this.service.render();
    }

    public async shareCapture() {
        this.showToast('공유 기능은 다음 제품 단계에서 연결돼요. 촬영 프레임은 전송되지 않았어요.');
        await this.service.render();
    }

    private async showToast(message: string) {
        this.toastMessage = message;
        await this.service.render();
        const current = message;
        setTimeout(async () => {
            if (this.toastMessage === current) {
                this.toastMessage = '';
                await this.service.render();
            }
        }, 2600);
    }
}

export default PageHomeComponent;